

import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'
import { isValidDateFormat } from '../utils/waterCalculations';
import { normalizeToIsoZMidnight } from '../utils/dateNormalization'
import { ensureFarmList } from '../utils/farmWizard'
import { lotApi } from '../utils/lotApi'
import { findFeedDaysWithoutRow } from '../utils/feedDaysWithoutRow'
import { ensureTodayProductionRow } from './crewMode/ensureTodayProductionRow'
import { readScopedString, writeScopedString } from '../utils/scopedStorage'
import { loadActiveContextSelection, persistActiveContextSelection } from '../utils/activeContextSelection'
import { findAvailableLotMatch } from '../utils/lotIdentity'
import type { ProductionSpeciesKey } from '../data/productionSpeciesPresets';
import GradientButton from '../components/GradientButton';
import type { MatrixData } from '../components/MatrixTable'
import ContextSelectorBar from '../components/ContextSelectorBar';
import CurrentContextBar from '../components/CurrentContextBar';
import DateTimeLocalInput from '../components/DateTimeLocalInput'
import { api } from '../utils/api'
import { feedLogApi } from '../utils/feedLogApi'
import { loadMortalityMatrix } from '../utils/mortalityMatrixApi'
import { getDefaultMatrixForSpecies } from '../utils/mortalityMatrix'
import {
  buildLastKnownWeightByRowId,
  buildLatestTemperatureByLotWeek,
  buildRecentTemperatureIndex,
  normalizeProjectionWaterLogs,
  resolveMortalityDot,
  resolveMortalityProjectionForRow,
  type ProjectionWaterRow
} from '../utils/mortalityProjection'
import { criticalDayOn, firstTransferAfterHatch, isHatcheryLot, lifeStageOn, readHatcheryDefaults, readLifeStage, readLifeStageHistory, type LifeStageChange, type LotLifeStage } from '../utils/hatchery'
import { getTimeZoneFromFarm, resolveTimeZone } from '../utils/timeBucketing'
import { formatLotPickerLabel, isLotActiveForInteraction } from '../utils/lotInteraction'
import {
  applyFeedKgDerivationToProductionRows,
  buildFeedDailyTotalsFromEvents,
  buildLotLabelByIdFromRows,
  clearFeedKgDerivedFromDeletedLots,
  updateFeedKgMetaAfterUserEdit,
  type FeedKgDerivationMeta
} from '../utils/productionFeedKgDerivation'
import {
  loadFeedKgDerivationMetaByRowId,
  saveFeedKgDerivationMetaByRowId
} from '../utils/productionFeedKgDerivationStorage'
import {
  loadAllProductionUnitTableRows,
  loadProductionUnitTableRows,
  readProductionUnitTableRowSyncStatus,
  refreshProductionUnitTableFromApi,
  retryProductionUnitTableRowSyncStatus,
  saveProductionUnitTableRows,
  updateProductionUnitTableRowEverywhere,
  deleteProductionUnitTableRowAndSync,
  deleteProductionUnitTableRowEverywhere,
  dequeueProductionRow,
  isRetiredSurvivalFieldKey,
  stripRetiredSurvivalColumns,
  upsertProductionUnitTableRowAndSync,
  type BiomassDataRow,
  type ProductionUnitTableContext
} from '../utils/productionUnitTable'
import type { EntityBatchSyncStatus } from '../utils/entityMutationSync'
import { useStoredPrincipal } from '../hooks/useStoredPrincipal'
import { getActiveTenantKey } from '../offline/tenant'
import { findFarmByIdOrName, findProductionUnitByIdOrName } from '../utils/contextSelection'
import { computeDisplayedEndOfDayStockByRowId } from '../utils/productionEndOfDayStock'
import { computeDailyMortalityRateByRowId } from '../utils/dailyMortalityRate'
import { resolveUnmappedLotUiState } from '../utils/unmappedLotUi'
import {
  PRODUCTION_SPECIES_PRESETS,
  getPresetColumns,
  loadStoredSpecies,
  persistSpeciesSelection
} from '../data/productionSpeciesPresets';
import { resolveFarmProductionSpeciesKey } from '../utils/farmProductionSpecies'
import { useT } from '../i18n'
import { getProductionSpeciesLabel } from '../utils/productionSpeciesLabels'
import {
  buildImportWorkspaceReviewNotice,
  parseImportWorkspaceReviewRouteState
} from './importWorkspace/importWorkspaceSession'
import {
  buildProductionRowLotReconciler,
  compareForSort,
  computeInsertAtForDailyMortality,
  ensureRowsHaveColumns,
  downloadTextBlob,
  isLotCellRecoveryEdit,
  normalizeDailyMortality,
  normalizeHealth,
  normalizeStage,
  normalizeTreatmentMort
} from './datatable/datatableHelpers'
import { buildProductionExportCsv } from './datatable/productionCsvExport'
import {
  deriveDatatableSyncOutcome,
  type DatatableSyncOutcome
} from '../utils/datatableSyncOutcome'
import { shouldRetryProductionSyncOnReconnect } from '../utils/productionReconnectRetry'
import { mortalityLogApi } from '../utils/mortalityLogApi'
import { weightSampleLogApi } from '../utils/weightSampleLogApi'
import { fishStockLogApi } from '../utils/fishStockLogApi'
import { harvestLogApi } from '../utils/harvestLogApi'
import type { FishStockLog, HarvestLog, MortalityLog, WeightSampleLog } from '../types/production'
import { computeMortalityRollupByRowId, findAllLinkedMortalityLogsForRow, overlayMortalityLogsOnProductionRows } from './biomass/mortalityLogBridge'
import {
  computeWeightSampleRollupByRowId,
  findAllLinkedWeightSampleLogsForRow,
  overlayWeightSamplesOnProductionRows
} from './biomass/weightSampleLogBridge'
import { overlayFishStockOnProductionRows } from './biomass/fishStockLogBridge'
import {
  countPlannedLinkedEventDeletes,
  executeProductionRecordLinkedEventDeletes,
  planProductionRecordLinkedEventDeletes
} from './biomass/productionRecordCascadeDelete'
import { DeleteProductionRowButton } from './biomass/DeleteProductionRowButton'
import { useTwoStepConfirm } from '../hooks/useTwoStepConfirm'
import { MortalityEventsPopover } from './biomass/MortalityEventsPopover'
import { SameDayRowsPopover } from './biomass/SameDayRowsPopover'
import { SamplingEventsPopover } from './biomass/SamplingEventsPopover'
import { computeSameDayRowsByRowId, findSameDayRowsForRow } from '../utils/sameDayProductionRows'
import {
  buildLegacyDuplicateGroups
} from '../utils/productionLegacyDuplicateGroups'
import {
  buildLegacyDuplicateMergeWeightSamplePayload,
  planAllLegacyDuplicateMerges,
  type LegacyDuplicateMergePlan
} from '../utils/productionLegacyDuplicateMergePlan'
import { LegacyDuplicateMigrationModal } from './datatable/LegacyDuplicateMigrationModal'
import { buildUnitSelectOptions } from '../utils/unitStatus'
import { farmDayKey, farmTimeZone, farmToday, formatFarmDateTime } from '../utils/farmTime'
import { buildLotFilterOptions, lotFilterValue } from './datatable/lotFilter'
import { buildLotExists, isHiddenEmptyDayRow } from '../utils/emptyDayRows'
import { dropEmptySameDayDuplicateRows } from '../utils/emptyDayRows'
import { buildDeletedLotRowMatcher, loadDeletedLots, mergeByRowLotLife, overlayByRowLotLife, splitLogsByDeletedLot, type DeletedLotRef } from '../utils/deletedLots'
import DeletedLotRowsLine from '../components/DeletedLotRowsLine'
import { findJoinedOutDestinations } from '../utils/movedLotRows'

const lifeStageFields = (metadata: unknown): { lifeStage?: LotLifeStage; lifeStageHistory?: LifeStageChange[] } => {
  const lot = { metadata: metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata as Record<string, unknown> : null }
  const lifeStage = readLifeStage(lot)
  const lifeStageHistory = readLifeStageHistory(lot)
  return {
    ...(lifeStage ? { lifeStage } : {}),
    ...(lifeStageHistory.length > 0 ? { lifeStageHistory } : {})
  }
}

const asStageLot = (lot: { lifeStage?: LotLifeStage; lifeStageHistory?: LifeStageChange[] } | undefined) => (
  lot ? { metadata: { lifeStage: lot.lifeStage, lifeStageHistory: lot.lifeStageHistory } } : null
)

const formatPct = (value: number): string => String(Number(value.toFixed(2)))

const findByIdString = (items: any[], id: string) => {
  return items.find((item) => {
    const raw = item?.id
    if (typeof raw === 'string' || typeof raw === 'number') return String(raw) === String(id)
    return false
  })
}

const uniqueColumns = (values: string[]): string[] => {
  const seen = new Set<string>()
  const out: string[] = []
  values.forEach((value) => {
    const key = String(value ?? '').trim()
    if (!key) return
    if (isRetiredSurvivalFieldKey(key)) return
    if (seen.has(key)) return
    seen.add(key)
    out.push(key)
  })
  return out
}

// Track A finding 3.1: a synthetic, render-only column showing the derived
// daily mortality % (count ÷ lot stock at the start of the day). It is spliced
// into the rendered column list (`displayColumns`) right after `dailyMortality`
// but never enters `columns` / `data` — it is not editable,
// not persisted, and not user-removable. See `renderDailyMortalityRateCell`.
const DAILY_MORTALITY_RATE_COL = 'dailyMortalityRate'

const withDailyMortalityRateColumn = (columns: string[]): string[] => {
  if (columns.includes(DAILY_MORTALITY_RATE_COL)) return columns
  const anchor = columns.indexOf('dailyMortality')
  if (anchor === -1) return columns
  const next = columns.slice()
  next.splice(anchor + 1, 0, DAILY_MORTALITY_RATE_COL)
  return next
}

const buildDatabaseColumns = (values: string[]): string[] =>
  ensureDailyMortalityColumn(stripRetiredSurvivalColumns(uniqueColumns(values)))

const ensureDailyMortalityColumn = (columns: string[]): string[] => {
  if (columns.includes('dailyMortality')) {
    return columns
  }
  const next = columns.slice()
  const weightIndex = next.indexOf('weight')
  const fishStockIndex = next.indexOf('fishStock')
  const insertAt = computeInsertAtForDailyMortality({
    weightIndex,
    fishStockIndex,
    length: next.length
  })
  next.splice(insertAt, 0, 'dailyMortality')
  return next
}

const ensureDailyMortalityField = (rows: BiomassDataRow[]): BiomassDataRow[] =>
  rows.map((row) => {
    if (row.dailyMortality !== undefined) return row
    return { ...row, dailyMortality: '' }
  })

const loadRowsForFarmAndUnit = (args: {
  farms: any[]
  farmId: string
  productionUnitId: string
}) => {
  const selectedFarm = findByIdString(args.farms, args.farmId)
  const selectedProductionUnit = findByIdString(selectedFarm?.productionUnits ?? [], args.productionUnitId)
  if (!selectedFarm || !selectedProductionUnit) return []

  let farmIdValue = ''
  if (typeof selectedFarm.id === 'string') farmIdValue = selectedFarm.id
  else if (typeof selectedFarm.id === 'number') farmIdValue = selectedFarm.id.toString()

  let productionUnitIdValue = ''
  if (typeof selectedProductionUnit.id === 'string') productionUnitIdValue = selectedProductionUnit.id
  else if (typeof selectedProductionUnit.id === 'number') productionUnitIdValue = selectedProductionUnit.id.toString()

  const context: ProductionUnitTableContext = {
    farmId: farmIdValue,
    productionUnitId: productionUnitIdValue,
    farmName: '',
    productionUnitName: ''
  }
  if (typeof selectedFarm.name === 'string') context.farmName = selectedFarm.name
  if (typeof selectedProductionUnit.name === 'string') context.productionUnitName = selectedProductionUnit.name
  return loadProductionUnitTableRows(context)
}

type NumericRule = {
  label: string
  min?: number
  max?: number
  allowDecimal?: boolean
  requireInteger?: boolean
}

const getNumericRules = (speciesKey: ProductionSpeciesKey): Record<string, NumericRule> => {
  const stockLabel = speciesKey === 'shrimp' ? 'PL count' : 'Fish stock'
  const isShrimp = speciesKey === 'shrimp'
  const stockMax = isShrimp ? 100_000_000 : 1_000_000
  const mortalityMax = isShrimp ? 100_000_000 : 1_000_000
  const harvestCountMax = isShrimp ? 100_000_000 : 1_000_000
  return {
    weight: { label: 'Weight (g)', min: 0, max: 10_000, allowDecimal: true },
    fishStock: { label: stockLabel, min: 0, max: stockMax, requireInteger: true },
    dailyMortality: { label: 'Daily mortality', min: 0, max: mortalityMax, requireInteger: true },
    harvestKg: { label: 'Harvest (kg)', min: 0, max: 10_000_000, allowDecimal: true },
    harvestCount: { label: 'Harvest (count)', min: 0, max: harvestCountMax, requireInteger: true }
  }
}

type NumericValidationResult =
  | { valid: true; normalized: string }
  | { valid: false; error: string }

const stripCommonGroupingChars = (value: string): string =>
  value
    // remove all whitespace including NBSP / narrow NBSP
    .replace(/\s+/g, '')
    // common grouping marks
    .replace(/[’']/g, '')
    .replaceAll('_', '')

const normalizeIntegerInput = (value: string): string => {
  const compact = stripCommonGroupingChars(value)

  const dotCount = (compact.match(/\./g) ?? []).length
  const commaCount = (compact.match(/,/g) ?? []).length

  // If both separators appear, treat them as grouping chars for integers.
  if (dotCount > 0 && commaCount > 0) {
    return compact.replaceAll('.', '').replaceAll(',', '')
  }

  const sep = dotCount > 0 ? '.' : commaCount > 0 ? ',' : ''
  if (!sep) return compact

  // Multiple separators => grouping.
  if ((sep === '.' ? dotCount : commaCount) > 1) {
    return compact.replaceAll(sep, '')
  }

  const [left, right = ''] = compact.split(sep)

  // Exactly 3 digits to the right is a strong signal for thousands grouping.
  if (/^\d{3}$/.test(right)) {
    return (left + right).replaceAll(sep, '')
  }

  // Otherwise treat as a decimal candidate; keep separator for parse.
  return sep === ',' ? `${left}.${right}` : compact
}

const normalizeDecimalInput = (value: string): string => {
  const compact = stripCommonGroupingChars(value)

  const lastDot = compact.lastIndexOf('.')
  const lastComma = compact.lastIndexOf(',')

  if (lastDot !== -1 && lastComma !== -1) {
    // Choose the last separator as decimal; the other becomes grouping.
    if (lastDot > lastComma) {
      // 1,234.56 => remove grouping commas
      return compact.replaceAll(',', '')
    }
    // 1.234,56 => remove grouping dots, convert decimal comma
    return compact.replaceAll('.', '').replaceAll(',', '.')
  }

  if (lastComma !== -1) {
    const commaCount = (compact.match(/,/g) ?? []).length
    if (commaCount > 1) {
      // 1,234,567 => grouping
      return compact.replaceAll(',', '')
    }
    const [left, right = ''] = compact.split(',')
    // If exactly 3 digits after comma, likely grouping (e.g. 1,234)
    if (/^\d{3}$/.test(right) && /^\d{1,3}$/.test(left)) {
      return left + right
    }
    // Otherwise treat comma as decimal
    return `${left}.${right}`
  }

  if (lastDot !== -1) {
    const dotCount = (compact.match(/\./g) ?? []).length
    if (dotCount > 1) {
      // 1.234.567 => grouping
      return compact.replaceAll('.', '')
    }
    const [left, right = ''] = compact.split('.')
    // If exactly 3 digits after dot, likely grouping (e.g. 55.200)
    if (/^\d{3}$/.test(right) && /^\d{1,3}$/.test(left)) {
      return left + right
    }
    return compact
  }

  return compact
}

const validateNumericValue = (
  column: string,
  rawValue: string,
  rules: Record<string, NumericRule>
): NumericValidationResult => {
  const rule = rules[column]
  if (!rule) {
    return { valid: true, normalized: rawValue }
  }

  const trimmed = (rawValue ?? '').toString().trim()
  if (trimmed === '') {
    return { valid: true, normalized: '' }
  }

  // Accept common locale formats like:
  // - 1,234 / 1.234 / 1 234 / 1<NBSP>234 (grouping: comma, dot, space, non-breaking space)
  // - 0,59 (decimal comma) / 0.59 (decimal dot)
  // while keeping integer-only columns strict.
  const sanitized = rule.requireInteger ? normalizeIntegerInput(trimmed) : normalizeDecimalInput(trimmed)
  const numeric = Number(sanitized)

  if (!Number.isFinite(numeric)) {
    return { valid: false, error: `${rule.label} must be a number.` }
  }

  if (rule.requireInteger && !Number.isInteger(numeric)) {
    return { valid: false, error: `${rule.label} must be a whole number.` }
  }

  if (!rule.allowDecimal && !rule.requireInteger && !Number.isInteger(numeric)) {
    return { valid: false, error: `${rule.label} must be a whole number.` }
  }

  if (rule.min !== undefined && numeric < rule.min) {
    return { valid: false, error: `${rule.label} must be at least ${rule.min}.` }
  }

  if (rule.max !== undefined && numeric > rule.max) {
    return { valid: false, error: `${rule.label} must be ${rule.max} or below.` }
  }

  const normalized = rule.allowDecimal ? numeric.toString() : Math.round(numeric).toString()
  return { valid: true, normalized }
}

const sanitizeNumericRows = (
  rows: BiomassDataRow[],
  rules: Record<string, NumericRule>
): { sanitized: BiomassDataRow[]; issues: Record<string, number>; examples: Record<string, string[]> } => {
  const issues: Record<string, number> = {}
  const examples: Record<string, string[]> = {}
  const sanitized = rows.map((row) => {
    const next = { ...row }
    Object.keys(rules).forEach((column) => {
      if (!(column in next)) return
      const raw = next[column] ?? ''
      const result = validateNumericValue(column, raw as any, rules)
      if (result.valid) {
        next[column] = result.normalized
      } else {
        issues[column] = (issues[column] ?? 0) + 1
        if ((examples[column]?.length ?? 0) < 3) {
          examples[column] = [...(examples[column] ?? []), String(raw)]
        }
        next[column] = ''
      }
    })
    return next
  })

  return { sanitized, issues, examples }
}

// Main component
export default function Datatable() {
  const location = useLocation()
  const navigate = useNavigate()
  const t = useT()
  const deepLink = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return {
      farmId: params.get('farmId')?.trim() || '',
      farmName: params.get('farmName')?.trim() || '',
      productionUnitId: params.get('productionUnitId')?.trim() || '',
      productionUnitName: params.get('productionUnitName')?.trim() || '',
      speciesKey: params.get('speciesKey')?.trim().toLowerCase() || ''
    }
  }, [location.search])
  const importReviewState = useMemo(() => parseImportWorkspaceReviewRouteState(location.search), [location.search])
  const importReviewNotice = useMemo(() => {
    if (importReviewState.importSource !== 'imports') return null
    return buildImportWorkspaceReviewNotice({
      rowLabel: 'production row',
      importedRows: importReviewState.importedRows,
      skippedRows: importReviewState.skippedRows,
      queuedRows: importReviewState.queuedRows,
      localOnlyRows: importReviewState.localOnlyRows,
      blockedRows: importReviewState.blockedRows,
      syncErrorMessage: importReviewState.syncErrorMessage,
      syncState: importReviewState.syncState
    })
  }, [importReviewState])

  // Capture tenant once per mount to avoid cross-tenant writes
  // if the session/tenant changes without a full reload.
  const [tenantKey] = useState(() => getActiveTenantKey())

  const principal = useStoredPrincipal()
  const canEditProductionRecords = !principal || principal.role === 'editor' || principal.role === 'admin'
  const canEditProductionRecordsRef = useRef(canEditProductionRecords)
  canEditProductionRecordsRef.current = canEditProductionRecords
  // State hooks
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [data, setData] = useState<BiomassDataRow[]>([]);
  const [feedKgMetaByRowId, setFeedKgMetaByRowId] = useState<Record<string, FeedKgDerivationMeta>>(() => loadFeedKgDerivationMetaByRowId())
  const [editingCell, setEditingCell] = useState<{ id: string; col: string } | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [speciesKey, setSpeciesKey] = useState<ProductionSpeciesKey>(() => loadStoredSpecies());
  const numericRules = useMemo(() => getNumericRules(speciesKey), [speciesKey]);
  const [columns, setColumns] = useState<string[]>(() =>
    buildDatabaseColumns(getPresetColumns(loadStoredSpecies()))
  );
  const [invalidDates, setInvalidDates] = useState<Set<string>>(new Set());
  const [showDateNotification, setShowDateNotification] = useState(false);
  const [lotImportNotice, setLotImportNotice] = useState<string | null>(null)
  const [farmId, setFarmId] = useState<string>('');
  const [productionUnitId, setProductionUnitId] = useState<string>('');
  const [availableFarms, setAvailableFarms] = useState<any[]>([]);
  const [availableProductionUnits, setAvailableProductionUnits] = useState<any[]>([]);
  const [availableLots, setAvailableLots] = useState<{ id: string; lotKey: string; stage?: string; hatchery?: boolean; lifeStage?: LotLifeStage; lifeStageHistory?: LifeStageChange[] }[]>([])
  // Farm-wide lot roster (all production units). Used only to tell a genuinely
  // orphaned lotId from one that was transferred to another unit — Track A
  // finding 4.2. Never drives the lot-edit dropdown (that stays PU-scoped).
  const [farmLots, setFarmLots] = useState<{ id: string; lotKey: string; stage?: string; hatchery?: boolean; productionUnitId?: string; unitless?: boolean; createdAt?: string }[]>([])
  const [foreignLotIds, setForeignLotIds] = useState<Set<string>>(new Set())
  // Findings #200: the farm's deleted lots. undefined = still loading; null =
  // not known on this device (offline before the first load).
  const [deletedLots, setDeletedLots] = useState<DeletedLotRef[] | null | undefined>(undefined)
  const [showDeletedLotRows, setShowDeletedLotRows] = useState(false)
  // Findings #200 follow-up: "Lote" filter ('' = all lots).
  const [lotFilter, setLotFilter] = useState('')
  // Findings #201: lots of this unit whose fish all joined the same lot in
  // another unit (lotId → destination unit id), and the switch for their rows.
  const [joinedOutLotUnits, setJoinedOutLotUnits] = useState<Map<string, string>>(new Map())
  const [showMovedLotRows, setShowMovedLotRows] = useState(false)
  const [projectionWaterRows, setProjectionWaterRows] = useState<ProjectionWaterRow[]>([])
  const [mortalityMatrixData, setMortalityMatrixData] = useState<MatrixData>(() => getDefaultMatrixForSpecies(loadStoredSpecies()))
  const [lotsLoading, setLotsLoading] = useState(false)
  const [lotAutoClearedIds, setLotAutoClearedIds] = useState<Set<string>>(new Set())
  const [isContextSwitching, setIsContextSwitching] = useState(false)
  const tableScrollRef = useRef<HTMLDivElement | null>(null)
  const tableScrollTopRef = useRef<HTMLDivElement | null>(null)
  const tableRef = useRef<HTMLTableElement | null>(null)
  const [tableWidth, setTableWidth] = useState<number>(0)
  const [productionSyncStatus, setProductionSyncStatus] = useState<EntityBatchSyncStatus | null>(null)
  const [retryingProductionSync, setRetryingProductionSync] = useState(false)
  const [refreshingProductionSync, setRefreshingProductionSync] = useState(false)
  const [productionSyncLastOutcome, setProductionSyncLastOutcome] = useState<(DatatableSyncOutcome & { attemptedAtIso: string }) | null>(null)
  const [storedMortalityLogs, setMortalityLogs] = useState<MortalityLog[]>([])
  // v2.1 finding #60 (WSL-06): server-backed per-event weight evidence. The
  // sidecar `samplingAggregateByRowId` state below is retained only for the
  // legacy fuse-duplicates migration path (WSL-08 removes it entirely).
  const [storedWeightSampleLogs, setWeightSampleLogs] = useState<WeightSampleLog[]>([])
  // T3-A-07a: server-backed per-event fish-stock evidence. Overlay-populates
  // `row.fishStock` when FishStockLog events exist for (calendar date, lot);
  // ProductionRecord.fishStock remains the fallback until T3-A-07b drops the
  // column and stops the write path.
  const [storedFishStockLogs, setFishStockLogs] = useState<FishStockLog[]>([])
  // PR2 (production-record cascade delete): HarvestLog was never wired into
  // this screen when T3-D landed, so `deleteRowById` used to orphan a row's
  // paired HarvestLog events (I-2 delete-parity gap). Fetched here only so
  // the shared cascade-delete helper can remove them; the harvest cell
  // overlay on Database is a separate T3-D follow-up.
  const [storedHarvestLogs, setHarvestLogs] = useState<HarvestLog[]>([])
  // Findings #200: rows of a deleted lot are hidden (nothing is changed or
  // deleted); "Mostrar" shows them greyed and read-only, Restaurar in Lotes
  // brings the lot back.
  const isDeletedLotRow = useMemo(
    () => buildDeletedLotRowMatcher(deletedLots ?? [], farmLots),
    [deletedLots, farmLots]
  )
  const deletedLotRowIds = useMemo(() => {
    const ids = new Set<string>()
    data.forEach((row: any) => {
      if (isDeletedLotRow(row, productionUnitId)) ids.add(String(row.id))
    })
    return ids
  }, [data, isDeletedLotRow, productionUnitId])
  // Findings #207: logs are matched to rows by day and lot name, so a deleted
  // lot's logs go only to its own rows (behind "Mostrar") and never count for
  // an active lot with the same name. `mortalityLogs` etc. are the active
  // lots' logs; `logsOfRowLot` picks the right set for one row.
  const mortalityLogSets = useMemo(() => splitLogsByDeletedLot(storedMortalityLogs, isDeletedLotRow), [storedMortalityLogs, isDeletedLotRow])
  const weightSampleLogSets = useMemo(() => splitLogsByDeletedLot(storedWeightSampleLogs, isDeletedLotRow), [storedWeightSampleLogs, isDeletedLotRow])
  const fishStockLogSets = useMemo(() => splitLogsByDeletedLot(storedFishStockLogs, isDeletedLotRow), [storedFishStockLogs, isDeletedLotRow])
  const harvestLogSets = useMemo(() => splitLogsByDeletedLot(storedHarvestLogs, isDeletedLotRow), [storedHarvestLogs, isDeletedLotRow])
  const mortalityLogs = mortalityLogSets.current
  const weightSampleLogs = weightSampleLogSets.current
  const fishStockLogs = fishStockLogSets.current
  const harvestLogs = harvestLogSets.current
  const logsOfRowLot = useCallback((row: { id?: unknown }) => {
    const life = deletedLotRowIds.has(String(row.id)) ? 'deleted' : 'current'
    return {
      mortalityLogs: mortalityLogSets[life],
      weightSampleLogs: weightSampleLogSets[life],
      fishStockLogs: fishStockLogSets[life],
      harvestLogs: harvestLogSets[life]
    }
  }, [deletedLotRowIds, mortalityLogSets, weightSampleLogSets, fishStockLogSets, harvestLogSets])
  const [mortalityEventsPopoverRowId, setMortalityEventsPopoverRowId] = useState<string | null>(null)
  const [sameDayRowsPopoverRowId, setSameDayRowsPopoverRowId] = useState<string | null>(null)
  // Two-step (button-flip) confirm for per-row delete — v2.1 finding #83:
  // modal/`window.confirm()` are suppressed by iOS Chrome in some contexts.
  const deleteRowConfirm = useTwoStepConfirm()
  // v2.1 finding #60 (WSL-08, 2026-07-01): the per-row sampling aggregate
  // used to live in a tenant-scoped localStorage sidecar. It has been
  // replaced by the server-backed `WeightSampleLog` table (WSL-01–WSL-06):
  // the popover fetches events from `weightSampleLogApi.list` and the
  // weight-cell badge count comes from the daily rollup overlay. No
  // per-row aggregate is held in component state here anymore.
  const [samplingEventsPopoverRowId, setSamplingEventsPopoverRowId] = useState<string | null>(null)
  // v2.1 finding #36: open the migration modal when the operator clicks the
  // "Fuse duplicates (N)" toolbar button. The modal previews every group
  // before any side effects fire.
  const [legacyDuplicateMigrationOpen, setLegacyDuplicateMigrationOpen] = useState(false)
  const [legacyDuplicateMigrationRunning, setLegacyDuplicateMigrationRunning] = useState(false)
  const [legacyDuplicateMigrationError, setLegacyDuplicateMigrationError] = useState<string | null>(null)

  const scrollDateInputIntoView = (input: HTMLInputElement) => {
    const container = tableScrollRef.current
    if (!container) return

    const inputRect = input.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    const padding = 12

    const leftOverflow = inputRect.left - containerRect.left
    const rightOverflow = inputRect.right - containerRect.right

    if (leftOverflow < padding) {
      container.scrollLeft += leftOverflow - padding
    } else if (rightOverflow > -padding) {
      container.scrollLeft += rightOverflow + padding
    }
  }
  const stockLabel = speciesKey === 'shrimp' ? t('datatable.column.plCount') : t('datatable.column.fishStock')

  // Column label mapping for display (keeps internal keys unchanged)
  const columnLabels: Record<string, string> = {
    dailyMortality: t('datatable.column.dailyMortality'),
    [DAILY_MORTALITY_RATE_COL]: t('datatable.column.dailyMortalityRate'),
    mort: t('datatable.column.mort'),
    do: t('datatable.column.do'),
    upVol: t('datatable.column.upVol'),
    fishStock: stockLabel,
    avgLengthMm: t('datatable.column.avgLengthMm'),
    feedKg: t('datatable.column.feedKg'),
    fcr: t('datatable.column.fcr'),
    feedProteinPct: t('datatable.column.feedProteinPct'),
    harvestKg: t('datatable.column.harvestKg'),
    harvestCount: t('datatable.column.harvestCount'),
    temperatureC: t('datatable.column.temperatureC'),
    salinityPpt: t('datatable.column.salinityPpt')
  };

  const speciesPresetLabel = farmId ? getProductionSpeciesLabel(t, speciesKey) : '—'

  useEffect(() => {
    const nextSpeciesKey = deepLink.speciesKey as ProductionSpeciesKey
    if (!deepLink.speciesKey) return
    if (!PRODUCTION_SPECIES_PRESETS[nextSpeciesKey]) return
    if (nextSpeciesKey === speciesKey) return
    setSpeciesKey(nextSpeciesKey)
  }, [deepLink.speciesKey, speciesKey])

  useEffect(() => {
    if (deepLink.speciesKey) return
    if (!farmId) return
    const selectedFarm = availableFarms.find((farm: any) => String(farm.id ?? farm.name ?? '') === String(farmId))
    const farmSpeciesKey = resolveFarmProductionSpeciesKey(selectedFarm)
    if (farmSpeciesKey && farmSpeciesKey !== speciesKey) {
      setSpeciesKey(farmSpeciesKey)
    }
  }, [availableFarms, deepLink.speciesKey, farmId, speciesKey])
  const [numericNotification, setNumericNotification] = useState<string | null>(null)
  const numericNoticeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const dismissNumericNotification = () => {
    if (numericNoticeTimeoutRef.current) {
      clearTimeout(numericNoticeTimeoutRef.current)
      numericNoticeTimeoutRef.current = null
    }
    setNumericNotification(null)
  }

  const showNumericNotification = (message: string) => {
    if (numericNoticeTimeoutRef.current) {
      clearTimeout(numericNoticeTimeoutRef.current)
    }
    setNumericNotification(message)
    numericNoticeTimeoutRef.current = setTimeout(dismissNumericNotification, 5000)
  }

  useEffect(() => {
    return () => {
      if (numericNoticeTimeoutRef.current) {
        clearTimeout(numericNoticeTimeoutRef.current)
        numericNoticeTimeoutRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    persistSpeciesSelection(speciesKey)
    const effective = buildDatabaseColumns(getPresetColumns(speciesKey))

    setColumns((prev) => {
      if (effective.length === prev.length && effective.every((value, index) => value === prev[index])) {
        return prev
      }
      return effective
    })

    setData((prev) => {
      if (prev.length === 0) return prev
      return ensureRowsHaveColumns(prev as any, effective) as any
    })
  }, [speciesKey])

  // MORT-12 trust signal: when one or more `MortalityLog` events match a row's
  // (calendar date, lot), the daily-mortality cell becomes a derived display of
  // the canonical events sum. The stored ProductionRecord.dailyMortality is
  // preserved on disk but not shown, and the cell becomes read-only so users
  // are nudged to edit individual events from the Biomass screen.
  const mortalityRollupByRowId = useMemo(
    () => mergeByRowLotLife(data, deletedLotRowIds, (rows, life) => computeMortalityRollupByRowId(rows, mortalityLogSets[life])),
    [data, deletedLotRowIds, mortalityLogSets]
  )

  // v2.1 finding #60 (WSL-06): sampling-badge event count now derives from the
  // server-backed `WeightSampleLog` set via the WSL-03 rollup helper. The
  // sidecar `samplingAggregateByRowId` remains referenced only by the legacy
  // fuse-duplicates migration path (WSL-08 removes it entirely).
  const weightSampleRollupByRowId = useMemo(
    () => mergeByRowLotLife(data, deletedLotRowIds, (rows, life) => computeWeightSampleRollupByRowId(rows, weightSampleLogSets[life])),
    [data, deletedLotRowIds, weightSampleLogSets]
  )

  // Compose the MORT-12 overlay before deriving end-of-day stock so that rows
  // whose mortality lives only as MortalityLog events (e.g. Biomass Sampling
  // saves with blank stored dailyMortality) still surface a derived fish stock.
  // Without this composition the derivation reads the raw blank `dailyMortality`
  // and gives up — see finding #6 in docs/release/v2.1/findings.md.
  //
  // v2.1 finding #60 (WSL-06): chain the WeightSampleLog overlay on top so the
  // weight cell reflects the server-backed same-day mean cross-device.
  //
  // T3-A-07a: chain the FishStockLog overlay OUTERMOST so `row.fishStock`
  // resolves to the latest FishStockLog `countedStock` on the row's
  // (calendar date, lot) when events exist. Order stays authority-first:
  // mortality → weight → fishStock. Rows without matching events fall
  // through unchanged with referential equality preserved.
  // Findings #192: a crew phone's empty row next to the office's row of the same lot and day is not shown.
  const sameDayDedupedData = useMemo(() => dropEmptySameDayDuplicateRows(data), [data])
  const rowsWithMortalityOverlay = useMemo(
    () => overlayByRowLotLife(sameDayDedupedData, deletedLotRowIds, (rows, life) => overlayFishStockOnProductionRows(
      overlayWeightSamplesOnProductionRows(
        overlayMortalityLogsOnProductionRows(rows, mortalityLogSets[life]),
        weightSampleLogSets[life]
      ),
      fishStockLogSets[life]
    )),
    [sameDayDedupedData, deletedLotRowIds, mortalityLogSets, weightSampleLogSets, fishStockLogSets]
  )

  // Read-only derivation: when fishStock is blank but the previous known stock
  // and current daily mortality (after MORT-12 overlay) are available, show
  // S_t = S_{t-1} - M_t in the cell. The stored row value remains blank; this
  // is a display-only contract matching the MORT-01 "Database auto-derived
  // stock" follow-up decision.
  const displayedEndOfDayStockByRowId = React.useMemo(
    () => computeDisplayedEndOfDayStockByRowId(rowsWithMortalityOverlay),
    [rowsWithMortalityOverlay]
  )

  // Track A finding 3.1: a read-only "daily mortality %" column (count ÷ lot
  // stock at the start of the day). Derived over the same overlay-composed rows
  // the stock derivation uses, so the two agree; never persisted.
  const dailyMortalityRateByRowId = React.useMemo(
    () => computeDailyMortalityRateByRowId(rowsWithMortalityOverlay),
    [rowsWithMortalityOverlay]
  )
  // Findings #138: a mortality-only day (crew) keeps the lot's last weight.
  const lastKnownWeightByRowId = React.useMemo(
    () => buildLastKnownWeightByRowId(rowsWithMortalityOverlay),
    [rowsWithMortalityOverlay]
  )

  // The rendered column list = the persisted/editable `columns` plus the
  // synthetic daily-mortality-% column. Only `<thead>` / `<tbody>` / the empty
  // colSpan and the CSV export use this; every schema / edit / persistence path
  // keeps using `columns`.
  const displayColumns = useMemo(() => withDailyMortalityRateColumn(columns), [columns])

  // Findings #91 / #92: past day rows that hold nothing (their records were
  // deleted) and empty rows of a deleted lot are hidden — nothing is deleted,
  // and "Mostrar" brings them back.
  const [showEmptyDayRows, setShowEmptyDayRows] = useState(false)
  const emptyDayRowContext = useMemo(() => {
    const zone = farmTimeZone(farmId)
    return { today: farmToday(zone), timeZone: zone, lotExists: lotsLoading ? undefined : buildLotExists(farmLots) }
  }, [farmId, farmLots, lotsLoading])

  // Sort data based on sortConfig.
  //
  // v2.1 finding (2026-07-09): sort/render the overlay-composed rows, not
  // the raw `data`. Post-T3-A-07b `data` always carries blank `weight` /
  // `dailyMortality` / `fishStock` cells (the ProductionRecord biology
  // cache columns are retired). Only the overlay chain fills those cells
  // in at render time. The single-event render paths for weight and the
  // anchor-row path for fishStock previously read raw `row[col]` and
  // returned blank; they now see the overlaid value. Row edit is blocked
  // for these three cells by the T3-A-07b guards in `beginEditCell`, so
  // reading from overlaid rows is safe (no risk of persisting a derived
  // value as if it were operator-typed). Non-overlaid columns render
  // identically because `overlay*OnProductionRows` preserve referential
  // equality for untouched rows.
  const sortedData = React.useMemo(() => {
    if (!sortConfig) return rowsWithMortalityOverlay;
    const sorted = [...rowsWithMortalityOverlay];
    if (sortConfig.key === DAILY_MORTALITY_RATE_COL) {
      // Synthetic column — its value is not on the row. Sort by the derived
      // rate, pushing rows without a rate to the bottom in both directions.
      sorted.sort((a, b) => {
        const aRate = dailyMortalityRateByRowId.get(String((a as any).id))?.ratePct ?? null
        const bRate = dailyMortalityRateByRowId.get(String((b as any).id))?.ratePct ?? null
        if (aRate === null && bRate === null) return 0
        if (aRate === null) return 1
        if (bRate === null) return -1
        return sortConfig.direction === 'asc' ? aRate - bRate : bRate - aRate
      });
      return sorted;
    }
    sorted.sort((a, b) => {
      return compareForSort({
        aValue: (a as any)[sortConfig.key],
        bValue: (b as any)[sortConfig.key],
        direction: sortConfig.direction
      })
    });
    return sorted;
  }, [rowsWithMortalityOverlay, sortConfig, dailyMortalityRateByRowId]);

  const hiddenDeletedLotRowCount = React.useMemo(
    () => sortedData.filter((row) => deletedLotRowIds.has(String((row as any).id))).length,
    [sortedData, deletedLotRowIds]
  )
  // Findings #201: rows of a lot that left this unit (the whole lot moved, or
  // its fish joined the same lot elsewhere) are hidden the same way, with their
  // destination: "26SETE1 (trasladado a B2)". rowId → destination unit name.
  const movedLotDestinationByRowId = useMemo(() => {
    const unitNameById = new Map(availableProductionUnits.map((unit: any) => [String(unit?.id ?? ''), String(unit?.name ?? '')] as const))
    const farmLotUnitById = new Map(farmLots.map((lot) => [String(lot.id), String(lot.productionUnitId ?? '')] as const))
    const out = new Map<string, string>()
    data.forEach((row: any) => {
      const rowId = String(row.id)
      if (deletedLotRowIds.has(rowId)) return
      const lotId = String(row.lotId ?? '').trim()
      const destinationUnitId = foreignLotIds.has(rowId)
        ? farmLotUnitById.get(lotId) ?? ''
        : (lotId ? joinedOutLotUnits.get(lotId) ?? null : null)
      if (destinationUnitId === null) return
      out.set(rowId, unitNameById.get(destinationUnitId) ?? '')
    })
    return out
  }, [data, deletedLotRowIds, foreignLotIds, joinedOutLotUnits, farmLots, availableProductionUnits])
  const hiddenMovedLotRowCount = React.useMemo(
    () => sortedData.filter((row) => movedLotDestinationByRowId.has(String((row as any).id))).length,
    [sortedData, movedLotDestinationByRowId]
  )
  const isReadOnlyLotRow = (rowId: string) => deletedLotRowIds.has(rowId) || movedLotDestinationByRowId.has(rowId)
  // Findings #206: a row of a deleted lot or of a lot that moved away answers
  // only to its own "Mostrar" line, even when it is also an empty past day, so
  // "Mostrando 1 fila…" always brings the row back.
  const hiddenEmptyDayRowCount = React.useMemo(
    () => sortedData.filter((row) => {
      const rowId = String((row as any).id)
      if (deletedLotRowIds.has(rowId) || movedLotDestinationByRowId.has(rowId)) return false
      return isHiddenEmptyDayRow(row as Record<string, unknown>, emptyDayRowContext)
    }).length,
    [sortedData, emptyDayRowContext, deletedLotRowIds, movedLotDestinationByRowId]
  )
  const lotFilterOptions = React.useMemo(() => {
    const zone = farmTimeZone(farmId)
    return buildLotFilterOptions({
      rows: sortedData as ReadonlyArray<Record<string, unknown>>,
      dayOf: (row) => farmDayKey(row.date, zone) ?? String(row.date ?? '').slice(0, 10),
      deletedRowIds: deletedLotRowIds,
      movedRowIds: new Set(movedLotDestinationByRowId.keys()),
      labels: {
        deleted: `(${t('datatable.deletedLot.tag')})`,
        moved: t('datatable.lotFilter.moved'),
        from: (day) => t('datatable.lotFilter.from', { day: formatFarmDateTime(day, zone, { dateOnly: true }) })
      }
    })
  }, [sortedData, farmId, deletedLotRowIds, movedLotDestinationByRowId, t])
  const activeLotFilter = lotFilterOptions.some((option) => option.value === lotFilter) ? lotFilter : ''

  const visibleData = React.useMemo(
    () => sortedData.filter((row) => {
      const rowId = String((row as any).id)
      if (activeLotFilter) {
        // A lot picked on purpose shows all its rows, also a deleted or
        // moved lot's (still greyed and read-only).
        if (lotFilterValue(row as Record<string, unknown>) !== activeLotFilter) return false
        if (deletedLotRowIds.has(rowId) || movedLotDestinationByRowId.has(rowId)) return true
      }
      if (deletedLotRowIds.has(rowId)) return showDeletedLotRows
      if (movedLotDestinationByRowId.has(rowId)) return showMovedLotRows
      if (showEmptyDayRows || hiddenEmptyDayRowCount === 0) return true
      return !isHiddenEmptyDayRow(row as Record<string, unknown>, emptyDayRowContext)
    }),
    [sortedData, activeLotFilter, showEmptyDayRows, hiddenEmptyDayRowCount, emptyDayRowContext, showDeletedLotRows, deletedLotRowIds, showMovedLotRows, movedLotDestinationByRowId]
  )

  // v2.1 finding #4: every Sampling save inserts a new ProductionRecord even
  // when several samplings land on the same (lot, calendar day). Until v2.2
  // redesigns the write semantics this map drives a transparency badge in the
  // Date cell so operators can see how many rows share the day and drill into
  // each sibling via SameDayRowsPopover.
  // v2.1 #47 follow-up: bucket by the farm-local calendar day, not the UTC
  // prefix. The original helper used `getUTC*` accessors so late-evening rows
  // stored as cross-UTC ISO-Z (e.g. `2026-05-02 21:00 BA local`
  // → `2026-05-03T00:00:00Z`) bucketed with the next morning's rows. Same
  // asymmetry as the dedup detector \u2014 fix mirrors the #47 plumbing.
  const sameDayRowsByRowId = useMemo(
    () => {
      const selectedFarm = availableFarms.find((farm: any) => String(farm?.id ?? '') === farmId)
      const timeZone = resolveTimeZone(getTimeZoneFromFarm(selectedFarm))
      return computeSameDayRowsByRowId(sameDayDedupedData, { timeZone })
    },
    [sameDayDedupedData, availableFarms, farmId]
  )

  // v2.1 finding #36: detect legacy duplicate `(lot, day)` rows so the
  // toolbar can show the "Fuse duplicates (N)" button. Rows created before
  // finding #12 landed (2026-06-19) can have multiple entries per lot/day,
  // which silently suppresses feed autofill in `productionFeedKgDerivation`.
  // v2.1 #47: pass the farm timezone so `(lot, day)` keys match the operator's
  // local calendar — otherwise late-evening rows stored as cross-UTC ISO-Z
  // would bucket with the next UTC day and falsely group as duplicates.
  const legacyDuplicateGroups = useMemo(
    () => {
      const selectedFarm = availableFarms.find((farm: any) => String(farm?.id ?? '') === farmId)
      const timeZone = resolveTimeZone(getTimeZoneFromFarm(selectedFarm))
      return buildLegacyDuplicateGroups(sameDayDedupedData as BiomassDataRow[], { timeZone })
    },
    [sameDayDedupedData, availableFarms, farmId]
  )
  const legacyDuplicateMergePlans = useMemo<LegacyDuplicateMergePlan[]>(
    () => planAllLegacyDuplicateMerges(legacyDuplicateGroups, mortalityLogs, fishStockLogs),
    [legacyDuplicateGroups, mortalityLogs, fishStockLogs]
  )

  const latestProjectionTemperatureByLotWeek = useMemo(
    () => buildLatestTemperatureByLotWeek(projectionWaterRows, availableLots),
    [projectionWaterRows, availableLots]
  )
  // Findings #138: the mortality dot compares with the lot's latest
  // temperature of the past 7 days (not only the same calendar week).
  const recentProjectionTemperatureIndex = useMemo(
    () => buildRecentTemperatureIndex(projectionWaterRows, availableLots),
    [projectionWaterRows, availableLots]
  )

  useEffect(() => {
    const tableEl = tableRef.current
    if (tableEl) {
      setTableWidth(tableEl.scrollWidth)
    }
  }, [columns, sortedData.length])

  const syncScroll = (source: 'top' | 'main') => (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollLeft } = event.currentTarget
    if (source !== 'main' && tableScrollRef.current && tableScrollRef.current.scrollLeft !== scrollLeft) {
      tableScrollRef.current.scrollLeft = scrollLeft
    }
    if (source !== 'top' && tableScrollTopRef.current && tableScrollTopRef.current.scrollLeft !== scrollLeft) {
      tableScrollTopRef.current.scrollLeft = scrollLeft
    }
  }

  const getFeedKgSourceNote = (rowId: string): { label: string; title: string } | null => {
    const meta = feedKgMetaByRowId[String(rowId)]
    if (!meta) return null
    if (meta.source === 'derived') {
      return { label: t('datatable.feedKg.autoLabel'), title: t('datatable.feedKg.autoTitle') }
    }
    if (meta.source === 'manual') {
      return { label: t('datatable.feedKg.manualLabel'), title: t('datatable.feedKg.manualTitle') }
    }
    return null
  }

  // ...existing code (effects, handlers, render, etc.)...



// Reusable styles (adapted from Water.tsx)
const styles = {
  input: {
    border: 'none',
    background: 'transparent',
    width: '100%',
    fontSize: '16px',
    padding: '6px 0',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  cell: {
    padding: '12px',
    border: '1px solid #ddd',
    background: '#fff',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    minWidth: '140px',
  },
  th: {
    padding: '15px 12px',
    border: '1px solid #ddd',
    textAlign: 'left' as const,
    fontWeight: 'bold',
    background: '#f8f9fa',
    minWidth: '140px',
  },
  actionCell: {
    padding: '10px',
    border: '1px solid #ddd',
    textAlign: 'center' as const,
    background: '#fff',
  },
  button: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    background: '#e9ecef',
    marginRight: '6px',
  },
  saveButton: {
    background: '#198754',
    color: '#fff',
  },
  cancelButton: {
    background: '#dc3545',
    color: '#fff',
  },
  editButton: {
    background: '#0d6efd',
    color: '#fff',
  },
}

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      const farms = await ensureFarmList()
      if (cancelled) return
      setAvailableFarms(farms)

      const activeContext = loadActiveContextSelection()
      const farmToken = deepLink.farmId || deepLink.farmName || activeContext.farmId || ''
      const resolvedFarm = findFarmByIdOrName(farms, farmToken) ?? farms[0]
      const resolvedFarmId = resolvedFarm?.id ? String(resolvedFarm.id) : ''
      if (resolvedFarmId && resolvedFarmId !== farmId) {
        setFarmId(resolvedFarmId)
      }

      const units = resolvedFarm?.productionUnits ?? []
      const productionUnitToken = deepLink.productionUnitId || deepLink.productionUnitName || activeContext.productionUnitId || ''
      const resolvedProductionUnit = findProductionUnitByIdOrName(resolvedFarm, productionUnitToken)
      const resolvedProductionUnitId = resolvedProductionUnit?.id ? String(resolvedProductionUnit.id) : String(units[0]?.id ?? '')
      if (resolvedProductionUnitId && resolvedProductionUnitId !== productionUnitId) {
        setProductionUnitId(resolvedProductionUnitId)
      }

      const initialRows =
        resolvedFarmId && resolvedProductionUnitId
          ? loadRowsForFarmAndUnit({ farms, farmId: resolvedFarmId, productionUnitId: resolvedProductionUnitId })
          : loadAllProductionUnitTableRows()

      const { sanitized, issues, examples } = sanitizeNumericRows(initialRows, numericRules)
      setData(sanitized)

      if (Object.keys(issues).length > 0) {
        if (import.meta.env.DEV) {
          // Helpful when a single farm/unit has locale-formatted numbers in storage.
          // Log both structured and stringified payloads so it's easy to copy/paste from DevTools.
          const payload = {
            speciesKey,
            farmId: resolvedFarmId,
            productionUnitId: resolvedProductionUnitId,
            issues,
            examples
          }
          console.warn('[Datatable] Numeric sanitization issues', payload)
          console.warn('[Datatable] Numeric sanitization issues (json)', JSON.stringify(payload, null, 2))
        }
        const detail = Object.entries(issues)
          .map(([column, count]) => `${columnLabels[column] ?? column}: ${count}`)
          .join(', ')
        showNumericNotification(`${t('datatable.numericNotice.storedPrefix')} (${detail}). ${t('datatable.numericNotice.storedSuffix')}`)
      }

      if (initialRows.length > 0 && (!columns || columns.length === 0)) {
        const keys = buildDatabaseColumns(Object.keys(initialRows[0]).filter(k => k !== 'id' && k !== 'lotId' && k !== 'unmappedLot'))
        setColumns(keys)
      }

      const invalidIds = new Set<string>()
      sanitized.forEach(row => {
        if (!isValidDateFormat(row.date)) invalidIds.add(row.id)
      })
      setInvalidDates(invalidIds)
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [deepLink.farmId, deepLink.farmName, deepLink.productionUnitId, deepLink.productionUnitName])

  useEffect(() => {
    if (farmId) {
      persistActiveContextSelection({ farmId })
    }
  }, [farmId])

  useEffect(() => {
    if (productionUnitId) {
      persistActiveContextSelection({ productionUnitId })
    }
  }, [productionUnitId])

  useEffect(() => {
    const selectedFarm = availableFarms.find((farm: any) => String(farm?.id ?? '') === farmId)
    if (!selectedFarm) {
      setAvailableProductionUnits([])
      if (productionUnitId) {
        setProductionUnitId('')
      }
      return
    }

    const units = selectedFarm.productionUnits ?? []
    setAvailableProductionUnits(units)

    if (units.length === 0) {
      if (productionUnitId) {
        setProductionUnitId('')
      }
      return
    }

    const unitToken = deepLink.productionUnitId || deepLink.productionUnitName || productionUnitId
    const resolvedProductionUnit = findProductionUnitByIdOrName(selectedFarm, unitToken)
    if (resolvedProductionUnit?.id) {
      const resolvedUnitId = String(resolvedProductionUnit.id)
      if (resolvedUnitId !== productionUnitId) {
        setProductionUnitId(resolvedUnitId)
      }
      return
    }

    const firstUnitId = String(units[0]?.id ?? '')
    if (!productionUnitId || !units.some((unit: any) => String(unit?.id ?? '') === productionUnitId)) {
      setProductionUnitId(firstUnitId)
    }
  }, [availableFarms, deepLink.productionUnitId, deepLink.productionUnitName, farmId, productionUnitId])

  const selectedFarmAndUnit = useMemo(() => {
    const selectedFarm = availableFarms.find((farm: any) => String(farm?.id ?? '') === farmId)
    const selectedProductionUnit = (selectedFarm?.productionUnits ?? []).find((pu: any) => String(pu?.id ?? '') === productionUnitId)
    if (!selectedFarm || !selectedProductionUnit) {
      return null
    }
    return {
      farmId: String(selectedFarm.id),
      productionUnitId: String(selectedProductionUnit.id),
      farmName: String(selectedFarm.name),
      productionUnitName: String(selectedProductionUnit.name)
    }
  }, [availableFarms, farmId, productionUnitId])

  const selectedProductionContext = useMemo<ProductionUnitTableContext | null>(() => {
    if (!selectedFarmAndUnit) return null
    return {
      farmId: selectedFarmAndUnit.farmId,
      productionUnitId: selectedFarmAndUnit.productionUnitId,
      farmName: selectedFarmAndUnit.farmName,
      productionUnitName: selectedFarmAndUnit.productionUnitName
    }
  }, [selectedFarmAndUnit])

  const currentProductionRowIds = useMemo(
    () => Array.from(new Set(data.map((row) => String(row.id)).filter(Boolean))),
    [data]
  )

  const formatSyncAttemptedAtLabel = (iso: string): string => {
    const parsed = Date.parse(iso)
    if (Number.isNaN(parsed)) return iso
    return new Date(parsed).toLocaleString()
  }

  const refreshCurrentProductionSyncStatus = async (): Promise<EntityBatchSyncStatus | null> => {
    if (!selectedProductionContext) {
      setProductionSyncStatus(null)
      return null
    }

    if (currentProductionRowIds.length === 0) {
      const emptyStatus: EntityBatchSyncStatus = {
        syncState: 'synced',
        blockedCount: 0,
        errorMessage: null,
        blockers: []
      }
      setProductionSyncStatus(emptyStatus)
      return emptyStatus
    }

    const nextStatus = await readProductionUnitTableRowSyncStatus(currentProductionRowIds)
    setProductionSyncStatus(nextStatus)
    return nextStatus
  }

  const lotKeyById = useMemo(() => {
    return new Map(availableLots.map((lot) => [String(lot.id), String(lot.lotKey ?? '')] as const))
  }, [availableLots])

  useEffect(() => {
    let cancelled = false

    const toRosterEntry = (lot: { id: unknown; lotKey?: unknown; stage?: string; hatchery?: unknown; metadata?: unknown; productionUnitId?: unknown; locationType?: unknown; unitless?: unknown; createdAt?: unknown }) => ({
      id: String(lot.id),
      lotKey: String(lot.lotKey ?? ''),
      stage: lot.stage,
      // Findings #200: the arrival record of "Sembrar en varias unidades" sits
      // in no unit; rows are never matched to it by name.
      ...(lot.productionUnitId ? { productionUnitId: String(lot.productionUnitId) } : {}),
      // Findings #207: a same-name deleted lot's logs are told apart by time.
      ...(lot.createdAt ? { createdAt: String(lot.createdAt) } : {}),
      unitless: lot.unitless === true || lot.locationType === 'farm',
      // Findings #138: egg / yolk-sac lots get no matrix comparison.
      hatchery: lot.hatchery === true || isHatcheryLot(lot as Parameters<typeof isHatcheryLot>[0]),
      // Findings #194: the stage on each row's day and the early critical days.
      ...lifeStageFields(lot.metadata)
    })

    const loadLotsForContext = async () => {
      if (!selectedFarmAndUnit) {
        setAvailableLots([])
        setFarmLots([])
        setLotAutoClearedIds(new Set())
        setForeignLotIds(new Set())
        return
      }

      const cacheKey = `lots-roster:${String(selectedFarmAndUnit.farmId)}:${String(selectedFarmAndUnit.productionUnitId)}`
      const farmCacheKey = `lots-roster-farm:${String(selectedFarmAndUnit.farmId)}`

      // Prefer cached lots immediately (supports offline / flaky networks).
      const cached = readScopedString(cacheKey)
      if (cached) {
        try {
          const parsed = JSON.parse(cached) as Array<{ id: string; lotKey: string; stage?: string }>
          if (Array.isArray(parsed)) {
            setAvailableLots(parsed.map(toRosterEntry))
          }
        } catch {
          // ignore
        }
      }
      const farmCached = readScopedString(farmCacheKey)
      if (farmCached) {
        try {
          const parsed = JSON.parse(farmCached) as Array<{ id: string; lotKey: string; stage?: string }>
          if (Array.isArray(parsed)) {
            setFarmLots(parsed.map(toRosterEntry))
          }
        } catch {
          // ignore
        }
      }

      setLotsLoading(true)
      try {
        const [lots, allFarmLots] = await Promise.all([
          lotApi.list({
            farmId: selectedFarmAndUnit.farmId,
            productionUnitId: selectedFarmAndUnit.productionUnitId
          }),
          lotApi.list({ farmId: selectedFarmAndUnit.farmId })
        ])
        if (cancelled) return
        const nextLots = lots.map(toRosterEntry)
        const nextFarmLots = allFarmLots.map(toRosterEntry)
        setAvailableLots(nextLots)
        setFarmLots(nextFarmLots)
        try {
          writeScopedString(cacheKey, JSON.stringify(nextLots))
          writeScopedString(farmCacheKey, JSON.stringify(nextFarmLots))
        } catch {
          // ignore
        }
      } catch (error) {
        if (cancelled) return
        // Keep any cached lots; do not wipe the roster on transient/offline failures.
        console.warn('[Datatable] Failed to refresh lots roster', error)
      } finally {
        if (!cancelled) setLotsLoading(false)
      }
    }

    void loadLotsForContext()

    return () => {
      cancelled = true
    }
  }, [selectedFarmAndUnit])

  const selectedFarmIdForDeletedLots = selectedFarmAndUnit ? String(selectedFarmAndUnit.farmId) : ''
  useEffect(() => {
    setDeletedLots(undefined)
    if (!selectedFarmIdForDeletedLots) return
    let cancelled = false
    void loadDeletedLots(selectedFarmIdForDeletedLots).then((lots) => {
      if (!cancelled) setDeletedLots(lots)
    })
    return () => {
      cancelled = true
    }
  }, [selectedFarmIdForDeletedLots])

  useEffect(() => {
    const inactiveIds = availableLots.filter((lot) => lot.stage === 'Inactive').map((lot) => String(lot.id))
    if (inactiveIds.length === 0) {
      setJoinedOutLotUnits((prev) => (prev.size === 0 ? prev : new Map()))
      return
    }
    let cancelled = false
    void lotApi.listLineageEventsForLots(inactiveIds)
      .then((events) => {
        if (!cancelled) setJoinedOutLotUnits(findJoinedOutDestinations(availableLots, events))
      })
      .catch(() => {
        // Display only: on error the rows simply stay visible.
      })
    return () => {
      cancelled = true
    }
  }, [availableLots])

  // Findings #194: the facility's daily mortality reference for early stages.
  const hatcheryDefaults = useMemo(
    () => readHatcheryDefaults(availableFarms.find((farm: any) => String(farm?.id ?? '') === String(farmId)) ?? null),
    [availableFarms, farmId]
  )

  // Findings #194: the first move after hatching of each early-stage lot, as a
  // farm day, for the dot's critical days.
  const [firstTransferByLotId, setFirstTransferByLotId] = useState<Map<string, string>>(() => new Map())
  useEffect(() => {
    const earlyLots = availableLots.filter((lot) => (lot.lifeStageHistory ?? []).some((change) => change.stage === 'yolkSac'))
    if (earlyLots.length === 0) {
      setFirstTransferByLotId((prev) => (prev.size === 0 ? prev : new Map()))
      return
    }
    let cancelled = false
    const zone = farmTimeZone(farmId)
    void lotApi.listLineageEventsForLots(earlyLots.map((lot) => String(lot.id)))
      .then((events) => {
        if (cancelled) return
        const next = new Map<string, string>()
        for (const lot of earlyLots) {
          const day = firstTransferAfterHatch({ id: String(lot.id), ...asStageLot(lot) }, events, (iso) => farmDayKey(iso, zone) ?? iso.slice(0, 10))
          if (day) next.set(String(lot.id), day)
        }
        setFirstTransferByLotId(next)
      })
      .catch(() => {
        // Display only: without events the move window is simply not marked.
      })
    return () => {
      cancelled = true
    }
  }, [availableLots, farmId])

  // MORT-12: refresh mortality logs whenever the (farm, productionUnit)
  // selection changes. Display-only feed — keep the lifecycle minimal (offline
  // preferred, ignore failures, never block the table render).
  //
  // v2.1 finding #57 (Phase 4 Session 2 Block A4): explicit Refresh/Retry/
  // reconnect paths must opt into `forceRemote: true` so cross-device
  // MortalityLog events surface even when the laptop's cache for the lot is
  // non-empty. The `mortalityLogApi.list` TTL gate (~30s) handles implicit
  // perf, but operator-driven Refresh and reconnect-retry must always pull.
  const refreshCurrentMortalityLogs = useCallback(async (options?: {
    forceRemote?: boolean
  }): Promise<boolean> => {
    if (!selectedFarmAndUnit) {
      setMortalityLogs([])
      return false
    }
    try {
      const logs = await mortalityLogApi.list({
        farmId: selectedFarmAndUnit.farmId,
        productionUnitId: selectedFarmAndUnit.productionUnitId
      }, {
        preferLocal: !options?.forceRemote,
        forceRemote: options?.forceRemote
      })
      setMortalityLogs(logs)
      return true
    } catch (error) {
      console.warn('[Datatable] Failed to load mortality logs', error)
      return false
    }
  }, [selectedFarmAndUnit])

  // v2.1 finding #60 (WSL-06): mirror the MortalityLog list-refresh path for
  // WeightSampleLog so the weight-cell overlay and same-day badge count reflect
  // the server-side event set. Same offline-preferred / forceRemote contract as
  // mortality — explicit Refresh/Retry/reconnect paths must opt into
  // `forceRemote: true`.
  const refreshCurrentWeightSampleLogs = useCallback(async (options?: {
    forceRemote?: boolean
  }): Promise<boolean> => {
    if (!selectedFarmAndUnit) {
      setWeightSampleLogs([])
      return false
    }
    try {
      const logs = await weightSampleLogApi.list({
        farmId: selectedFarmAndUnit.farmId,
        productionUnitId: selectedFarmAndUnit.productionUnitId
      }, {
        preferLocal: !options?.forceRemote,
        forceRemote: options?.forceRemote
      })
      setWeightSampleLogs(logs)
      return true
    } catch (error) {
      console.warn('[Datatable] Failed to load weight sample logs', error)
      return false
    }
  }, [selectedFarmAndUnit])

  // T3-A-07a: mirror the WSL/MortalityLog list-refresh path for FishStockLog
  // so the fish-stock cell overlay reflects the server-side event set.
  // Same offline-preferred / forceRemote contract as WSL. Read-path safety
  // net for T3-A-07b (ProductionRecord.fishStock column retirement).
  const refreshCurrentFishStockLogs = useCallback(async (options?: {
    forceRemote?: boolean
  }): Promise<boolean> => {
    if (!selectedFarmAndUnit) {
      setFishStockLogs([])
      return false
    }
    try {
      const logs = await fishStockLogApi.list({
        farmId: selectedFarmAndUnit.farmId,
        productionUnitId: selectedFarmAndUnit.productionUnitId
      }, {
        preferLocal: !options?.forceRemote,
        forceRemote: options?.forceRemote
      })
      setFishStockLogs(logs)
      return true
    } catch (error) {
      console.warn('[Datatable] Failed to load fish stock logs', error)
      return false
    }
  }, [selectedFarmAndUnit])

  // PR2: mirror the FishStockLog refresh path for HarvestLog. Fetched only so
  // `deleteRowById`'s shared cascade-delete can remove a row's paired harvest
  // events (I-2 delete-parity — the gap this screen shipped with after T3-D).
  const refreshCurrentHarvestLogs = useCallback(async (options?: {
    forceRemote?: boolean
  }): Promise<boolean> => {
    if (!selectedFarmAndUnit) {
      setHarvestLogs([])
      return false
    }
    try {
      const logs = await harvestLogApi.list({
        farmId: selectedFarmAndUnit.farmId,
        productionUnitId: selectedFarmAndUnit.productionUnitId
      }, {
        preferLocal: !options?.forceRemote,
        forceRemote: options?.forceRemote
      })
      setHarvestLogs(logs)
      return true
    } catch (error) {
      console.warn('[Datatable] Failed to load harvest logs', error)
      return false
    }
  }, [selectedFarmAndUnit])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const ok = await refreshCurrentMortalityLogs()
      if (cancelled && ok) {
        // No-op: refreshCurrentMortalityLogs already wrote state; if the effect
        // was cancelled mid-await, React will discard the stale render.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [refreshCurrentMortalityLogs])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const ok = await refreshCurrentWeightSampleLogs()
      if (cancelled && ok) {
        // No-op: same rationale as the MortalityLog effect above.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [refreshCurrentWeightSampleLogs])

  // T3-A-07a: mount-effect mirror for FishStockLog so the overlay is
  // populated as soon as the (farm, productionUnit) selection resolves.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const ok = await refreshCurrentFishStockLogs()
      if (cancelled && ok) {
        // No-op: same rationale as the WSL effect above.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [refreshCurrentFishStockLogs])

  // PR2: mount-effect mirror for HarvestLog (delete-parity feed only).
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const ok = await refreshCurrentHarvestLogs()
      if (cancelled && ok) {
        // No-op: same rationale as the FishStockLog effect above.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [refreshCurrentHarvestLogs])

  useEffect(() => {
    void refreshCurrentProductionSyncStatus().catch(() => {
      // Keep Datatable usable even if sync-status reads fail.
    })
  }, [currentProductionRowIds, selectedProductionContext])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const retryIfWarranted = () => {
      const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine
      if (!shouldRetryProductionSyncOnReconnect({
        status: productionSyncStatus,
        isOnline,
        hasRowIds: currentProductionRowIds.length > 0
      })) return
      if (!selectedProductionContext) return

      void (async () => {
        try {
          const nextStatus = await retryProductionUnitTableRowSyncStatus(currentProductionRowIds)
          setProductionSyncStatus(nextStatus)

          if (nextStatus.syncState === 'synced' && nextStatus.blockedCount === 0) {
            const refreshedRows = await refreshProductionUnitTableFromApi(selectedProductionContext)
            const { sanitized } = sanitizeNumericRows(refreshedRows, numericRules)
            setData(sanitized)
            // v2.1 finding #57: reconnect-retry must also pull peer MortalityLog.
            await refreshCurrentMortalityLogs({ forceRemote: true })
          }
        } catch {
          // Keep Datatable usable; reconnect-driven retries are best-effort and the
          // explicit Retry sync control remains available with full outcome messaging.
        }
      })()
    }

    const onOnline = () => retryIfWarranted()
    const onFocus = () => retryIfWarranted()
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      retryIfWarranted()
    }

    window.addEventListener('online', onOnline)
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [productionSyncStatus, currentProductionRowIds, selectedProductionContext, numericRules])

  useEffect(() => {
    if (!selectedFarmAndUnit) {
      setProjectionWaterRows([])
      setMortalityMatrixData(getDefaultMatrixForSpecies(speciesKey))
      return
    }

    let cancelled = false

    void (async () => {
      const [waterLogsResult, mortalityMatrixResult] = await Promise.allSettled([
        api.waterLogs.list({
          farmId: selectedFarmAndUnit.farmId,
          productionUnitId: selectedFarmAndUnit.productionUnitId
        }),
        loadMortalityMatrix(speciesKey, { preferRecentLocal: true })
      ])

      if (cancelled) return

      if (waterLogsResult.status === 'fulfilled') {
        setProjectionWaterRows(normalizeProjectionWaterLogs(waterLogsResult.value))
      } else {
        console.warn('[Datatable] Failed to load water logs for mortality guidance', waterLogsResult.reason)
        setProjectionWaterRows([])
      }

      if (mortalityMatrixResult.status === 'fulfilled') {
        setMortalityMatrixData(mortalityMatrixResult.value.matrix)
      } else {
        console.warn('[Datatable] Failed to load mortality matrix for guidance', mortalityMatrixResult.reason)
        setMortalityMatrixData(getDefaultMatrixForSpecies(speciesKey))
      }
    })()

    return () => {
      cancelled = true
    }
  }, [selectedFarmAndUnit, speciesKey])

  useEffect(() => {
    // Safe migration rule: if a specific farm+PU is selected and we have a
    // roster, reconcile each row's lot fields against it. A lotId that is not
    // in THIS unit's roster is only "Sin mapear" when it is also unknown
    // farm-wide — one that resolves to a lot in another unit was transferred
    // out and keeps its history here (Track A finding 4.2).
    if (!selectedFarmAndUnit) return
    if (availableLots.length === 0) return
    // Wait for the farm-wide roster before reconciling: acting on only the
    // PU roster is exactly what wrongly relabels transferred-out lots. Once
    // the load settles we proceed even with an empty farm roster (a farm with
    // no lots has nothing to clear anyway).
    if (lotsLoading && farmLots.length === 0) return
    // Findings #200: wait for the deleted lots too, so their rows are left
    // as they are instead of being re-linked by name.
    if (deletedLots === undefined) return
    if (data.length === 0) {
      if (lotAutoClearedIds.size > 0) setLotAutoClearedIds(new Set())
      if (foreignLotIds.size > 0) setForeignLotIds(new Set())
      return
    }

    const unitId = String(selectedFarmAndUnit.productionUnitId)
    const reconcile = buildProductionRowLotReconciler(availableLots, farmLots, {
      isDeletedLotRow: (row) => isDeletedLotRow(row, unitId)
    })
    let changed = false
    const cleared = new Set<string>()
    const foreign = new Set<string>()
    const next = data.map((row: any) => {
      const resolution = reconcile(row)
      if (resolution.isForeignLot) foreign.add(String(row.id))
      if (!resolution.changed) return row
      changed = true
      if (resolution.cleared) cleared.add(String(row.id))
      return { ...row, ...resolution.fields }
    })

    const foreignChanged = foreign.size !== foreignLotIds.size
      || [...foreign].some((id) => !foreignLotIds.has(id))
    if (foreignChanged) setForeignLotIds(foreign)

    if (changed) {
      setData(next)
      setLotAutoClearedIds(cleared)
      const context: ProductionUnitTableContext = {
        farmId: selectedFarmAndUnit.farmId,
        productionUnitId: selectedFarmAndUnit.productionUnitId,
        farmName: selectedFarmAndUnit.farmName,
        productionUnitName: selectedFarmAndUnit.productionUnitName
      }
      saveProductionUnitTableRows(context, next as any)
    } else if (lotAutoClearedIds.size > 0) {
      setLotAutoClearedIds(new Set())
    }
  }, [availableLots, farmLots, lotsLoading, data, selectedFarmAndUnit, deletedLots, isDeletedLotRow])

  // When farmName changes, populate production units for selection
  useEffect(() => {
    const selectedFarm = availableFarms.find((farm: any) => String(farm?.id ?? '') === farmId)
    if (selectedFarm) {
      const productionUnits = selectedFarm.productionUnits ?? []
      setAvailableProductionUnits(productionUnits)
      if (productionUnits.length === 0) {
        setProductionUnitId('')
      } else if (!productionUnitId || !productionUnits.some((pu: any) => String(pu?.id ?? '') === productionUnitId)) {
        setProductionUnitId(String(productionUnits[0].id ?? ''))
      }
    } else {
      setAvailableProductionUnits([])
      setProductionUnitId('')
    }
  }, [farmId, productionUnitId, availableFarms])

  // When production unit selection changes, load data for that PU
  useEffect(() => {
    let cancelled = false

    const applyDerivedFeedKgIfPossible = async (
      context: ProductionUnitTableContext,
      initialRows: BiomassDataRow[],
      options?: { createMissingDayRows?: boolean }
    ) => {
      try {
        let baseRows = initialRows
        const [allEvents, deletedFarmLots, farmLotList] = await Promise.all([
          feedLogApi.list({ farmId: context.farmId, productionUnitId: context.productionUnitId }),
          loadDeletedLots(context.farmId).catch(() => null),
          lotApi.list({ farmId: context.farmId }).catch(() => [])
        ])
        if (cancelled) return
        // Findings #207: feed totals are matched to rows by day and lot name,
        // so a deleted lot's feedings fill only its own rows and never an
        // active lot's row with the same name.
        const isDeletedFeedLot = buildDeletedLotRowMatcher(deletedFarmLots ?? [], farmLotList.map((lot) => ({
          id: String(lot.id),
          lotKey: String(lot.lotKey ?? ''),
          ...(lot.productionUnitId ? { productionUnitId: String(lot.productionUnitId) } : {}),
          ...(lot.createdAt ? { createdAt: String(lot.createdAt) } : {})
        })))
        const eventSets = splitLogsByDeletedLot(allEvents, isDeletedFeedLot)
        const events = eventSets.current

        // Findings #141: a day with feedings and no row (office feedings, crew
        // feedings before #256) gets its row, so its kg shows — only after
        // the server refresh, and only for someone who can edit rows.
        if (options?.createMissingDayRows && canEditProductionRecordsRef.current) {
          const unitLots = await lotApi.list({ farmId: context.farmId, productionUnitId: context.productionUnitId }).catch(() => [])
          if (cancelled) return
          const missing = findFeedDaysWithoutRow({
            rows: baseRows,
            feedLogs: events,
            lots: unitLots.map((lot) => ({ id: String(lot.id), lotKey: String(lot.lotKey ?? '') })),
            timeZone: farmTimeZone(context.farmId)
          })
          if (missing.length > 0) {
            for (const entry of missing) {
              // eslint-disable-next-line no-await-in-loop
              await ensureTodayProductionRow(context, { id: entry.lotId, lotKey: entry.lotKey }, entry.dayKey).catch(() => {})
            }
            if (cancelled) return
            baseRows = sanitizeNumericRows(loadProductionUnitTableRows(context), numericRules).sanitized
            setData(baseRows)
          }
        }

        const selectedFarm = availableFarms.find((farm: any) => String(farm?.id ?? '') === context.farmId)
        const timeZone = resolveTimeZone(getTimeZoneFromFarm(selectedFarm))
        const lotLabelById = buildLotLabelByIdFromRows(baseRows)
        const feedTotals = {
          current: buildFeedDailyTotalsFromEvents(eventSets.current, { timeZone, lotLabelById }),
          deleted: buildFeedDailyTotalsFromEvents(eventSets.deleted, { timeZone, lotLabelById })
        }
        const metaByRowId = loadFeedKgDerivationMetaByRowId()
        setFeedKgMetaByRowId(metaByRowId)
        const nextMetaByRowId = { ...metaByRowId }
        const deletedRowIds = new Set(baseRows.filter((row) => isDeletedFeedLot(row, context.productionUnitId)).map((row) => String(row.id)))
        const result = { rows: baseRows, metaByRowId: nextMetaByRowId, rowsChanged: false, metaChanged: false }
        result.rows = overlayByRowLotLife(baseRows, deletedRowIds, (rows, life) => {
          const applied = applyFeedKgDerivationToProductionRows<BiomassDataRow>({
            rows,
            feedTotals: feedTotals[life],
            metaByRowId: nextMetaByRowId,
            nowIso: new Date().toISOString(),
            timeZone
          })
          const cleared = life === 'current'
            ? clearFeedKgDerivedFromDeletedLots<BiomassDataRow>({
              rows: applied.rows,
              currentTotals: feedTotals.current,
              deletedTotals: feedTotals.deleted,
              metaByRowId: nextMetaByRowId,
              timeZone
            })
            : null
          result.rowsChanged = result.rowsChanged || applied.rowsChanged || Boolean(cleared?.rowsChanged)
          result.metaChanged = result.metaChanged || applied.metaChanged || Boolean(cleared?.metaChanged)
          return cleared ? cleared.rows : applied.rows
        })

        if (cancelled) return
        if (!result.rowsChanged && !result.metaChanged) return

        if (result.rowsChanged) {
          setData(result.rows)
          saveProductionUnitTableRows(context, result.rows as any)
        }

        if (result.metaChanged) {
          saveFeedKgDerivationMetaByRowId(result.metaByRowId)
          setFeedKgMetaByRowId(result.metaByRowId)
        }
      } catch {
        // best-effort; Datatable remains usable without derived feed
      }
    }

    if (farmId && productionUnitId) {
      const selectedFarm = availableFarms.find((farm: any) => String(farm?.id ?? '') === farmId)
      const selectedProductionUnit = (selectedFarm?.productionUnits ?? []).find((pu: any) => String(pu?.id ?? '') === productionUnitId)
      if (!selectedFarm || !selectedProductionUnit) {
        setData([])
        setInvalidDates(new Set())
        setIsContextSwitching(false)
        return
      }

      const context: ProductionUnitTableContext = {
        farmId: String(selectedFarm.id),
        productionUnitId: String(selectedProductionUnit.id),
        farmName: String(selectedFarm.name),
        productionUnitName: String(selectedProductionUnit.name)
      }

      const rows = loadProductionUnitTableRows(context)
      const { sanitized, issues } = sanitizeNumericRows(rows, numericRules)
      setData(sanitized)
      setFeedKgMetaByRowId(loadFeedKgDerivationMetaByRowId())
      setIsContextSwitching(false)
      const invalidIds = new Set<string>()
      sanitized.forEach(row => {
        if (!isValidDateFormat(row.date)) invalidIds.add(row.id)
      })
      setInvalidDates(invalidIds)

      // Non-blocking refresh from API when online.
      // cancelled flag prevents a stale response from a previous PU from overwriting current data.
      void refreshProductionUnitTableFromApi(context)
        .then((refreshedRows) => {
          if (cancelled) return
          const { sanitized: refreshedSanitized } = sanitizeNumericRows(refreshedRows, numericRules)
          setData(refreshedSanitized)

          // Apply FeedLog-derived feedKg best-effort after refresh.
          void applyDerivedFeedKgIfPossible(context, refreshedSanitized, { createMissingDayRows: true })
        })
        .catch(() => {
          // ignore; local-first
        })

      // Apply FeedLog-derived feedKg best-effort on initial local load.
      void applyDerivedFeedKgIfPossible(context, sanitized)

      if (Object.keys(issues).length > 0) {
        const detail = Object.entries(issues)
          .map(([column, count]) => `${columnLabels[column] ?? column}: ${count}`)
          .join(', ')
        showNumericNotification(`${t('datatable.numericNotice.storedPrefix')} (${detail}). ${t('datatable.numericNotice.storedSuffix')}`)
      }
      // set columns from rows if present
      if (rows.length > 0 && (!columns || columns.length === 0)) {
        setColumns(buildDatabaseColumns(Object.keys(rows[0]).filter(k => k !== 'id' && k !== 'lotId' && k !== 'unmappedLot')))
      }
    } else {
      // When selection is temporarily unset (e.g. during context switching),
      // do not sanitize "all rows" across farms/species using the current species rules.
      // That produces confusing false-positive banners (e.g. shrimp-scale values flagged under trout caps).
      setData([])
      setInvalidDates(new Set())
      setIsContextSwitching(false)
      if (numericNotification) dismissNumericNotification()
    }

    return () => {
      cancelled = true
    }
    // v2.1 #43: include `importReviewState` so the loader re-fires when the
    // user returns from Import Workspace with `?importSource=imports&...`.
    // Without this nudge, when the imported context matches the current
    // selection neither `farmId` nor `productionUnitId` change identity, so
    // the freshly-saved rows in localStorage are never re-read into `data`
    // and the table looks empty until a hard reload.
  }, [farmId, productionUnitId, importReviewState])

  const normalizeDateForSave = (rowId: string, raw: string): string => {
    let value = raw
    const normalized = normalizeToIsoZMidnight(value)
    if (normalized.ok) value = normalized.isoZ

    const valid = isValidDateFormat(value)
    setInvalidDates((prev) => {
      const cp = new Set(prev)
      if (!valid && value.trim() !== '') cp.add(rowId)
      else cp.delete(rowId)
      return cp
    })

    if (!valid && value.trim() !== '') {
      setShowDateNotification(true)
      setTimeout(() => setShowDateNotification(false), 5000)
    }

    return value
  }

  const normalizeValueForSave = (args: { rowId: string; col: string; raw: string }): string => {
    const { rowId, col, raw } = args
    switch (col) {
      case 'date':
        return normalizeDateForSave(rowId, raw)
      case 'stage':
        return normalizeStage(raw)
      case 'dailyMortality':
        return normalizeDailyMortality(raw)
      case 'health':
        return normalizeHealth(raw)
      case 'mort':
        return normalizeTreatmentMort(raw)
      default:
        return raw
    }
  }

  const getEditContext = (): ProductionUnitTableContext | null => {
    if (!selectedFarmAndUnit) return null
    return {
      farmId: selectedFarmAndUnit.farmId,
      productionUnitId: selectedFarmAndUnit.productionUnitId,
      farmName: selectedFarmAndUnit.farmName,
      productionUnitName: selectedFarmAndUnit.productionUnitName
    }
  }

  const patchRowsById = (
    rows: BiomassDataRow[],
    rowId: string,
    patch: Record<string, unknown>
  ): BiomassDataRow[] => rows.map((row) => (row.id === rowId ? { ...row, ...(patch as any) } : row))

  const clearLotAutoClearedId = (rowId: string): void => {
    if (!lotAutoClearedIds.has(rowId)) return
    setLotAutoClearedIds((prev) => {
      const cp = new Set(prev)
      cp.delete(rowId)
      return cp
    })
  }

  const persistRowsOrPatchEverywhere = (args: {
    context: ProductionUnitTableContext | null
    rowId: string
    nextRows: BiomassDataRow[]
    patch: Partial<BiomassDataRow>
  }): void => {
    const { context, rowId, nextRows, patch } = args
    if (context) {
      saveProductionUnitTableRows(context, nextRows)
    } else {
      updateProductionUnitTableRowEverywhere(rowId, patch)
    }
  }

  const saveLotCell = (args: {
    rowId: string
    raw: string
    currentRow: any
    context: ProductionUnitTableContext | null
  }): void => {
    const { rowId, raw, currentRow, context } = args

    // I-3 extension (U4, docs/lot-lifecycle-vs-concerns-audit.md §3): reject
    // any `lot` cell patch that isn't the unmapped-label recovery flow. Direct
    // reassignment of an already-linked row belongs at the original capture
    // screen — doing it here would orphan the Tier A events still pointing at
    // the row's original lot.
    if (!isLotCellRecoveryEdit(currentRow)) {
      showNumericNotification(t('datatable.error.lotEditOutsideRecovery'))
      return
    }

    // When roster exists, raw is the selected lotId.
    if (selectedFarmAndUnit && availableLots.length > 0) {
      const nextLotId = String(raw ?? '').trim()
      const selectedLot = nextLotId
        ? availableLots.find((lot) => String(lot.id) === nextLotId) ?? null
        : null

      if (selectedLot && !isLotActiveForInteraction(selectedLot.stage)) {
        showNumericNotification(t('datatable.error.activeLotRequired'))
        return
      }

      const nextLotKey = nextLotId ? (lotKeyById.get(nextLotId) ?? '') : ''

      const same = String(currentRow.lot ?? '') === nextLotKey && String(currentRow.lotId ?? '') === nextLotId
      if (same) return

      const patch = { lot: nextLotKey, lotId: nextLotId, unmappedLot: '' }
      const nextRows = patchRowsById(data, rowId, patch)
      setData(nextRows)
      clearLotAutoClearedId(rowId)
      persistRowsOrPatchEverywhere({ context, rowId, nextRows, patch: { lot: nextLotKey, lotId: nextLotId } })
      return
    }

    // Free-text fallback (no roster): store label, clear lotId.
    const nextLotKey = String(raw ?? '')
    const same = String(currentRow.lot ?? '') === nextLotKey && String(currentRow.lotId ?? '') === ''
    if (same) return

    const patch = { lot: nextLotKey, lotId: '', unmappedLot: '' }
    const nextRows = patchRowsById(data, rowId, patch)
    setData(nextRows)
    persistRowsOrPatchEverywhere({ context, rowId, nextRows, patch: { lot: nextLotKey, lotId: '' } })
  }

  // Save a single cell's value to data and localStorage
  function saveCell(id: string, col: string, incomingValue: string): boolean {
    if (!canEditProductionRecords) {
      showNumericNotification(t('datatable.readonly.cannotEditRecords'))
      return false
    }

    const raw = String(incomingValue ?? '')
    const context = getEditContext()
    const currentRow = data.find((row) => row.id === id) as any
    if (!currentRow) return false

    if (col === 'lot') {
      saveLotCell({ rowId: id, raw, currentRow, context })
      if (numericNotification) dismissNumericNotification()
      return true
    }

    // v2.2 findings.md #23 follow-up: the `date` cell renders via a
    // dedicated `isDateCol` branch in `renderBodyCell` (`DateTimeLocalInput`,
    // `onChangeValue` -> `saveCell` directly) that never goes through
    // `beginEditCell` — so the guard added there for `date` never actually
    // fires through the real UI. This is the one enforcement point every
    // `date` write reaches unconditionally; the `beginEditCell` guard stays
    // too as defense-in-depth for any other caller.
    if (col === 'date') {
      showNumericNotification(t('datatable.date.readOnlyEditAttempt'))
      return false
    }

    let value = normalizeValueForSave({ rowId: id, col, raw })

    const numericCheck = validateNumericValue(col, value, numericRules)
    if (!numericCheck.valid) {
      showNumericNotification(numericCheck.error)
      return false
    }
    value = numericCheck.normalized

    if (String(currentRow[col] ?? '') === String(value ?? '')) return true

    const nextRows = patchRowsById(data, id, { [col]: value })
    setData(nextRows)

    if (col === 'feedKg') {
      const nowIso = new Date().toISOString()
      const existingMeta = loadFeedKgDerivationMetaByRowId()
      const updatedMeta = updateFeedKgMetaAfterUserEdit(existingMeta, id, value, nowIso)
      saveFeedKgDerivationMetaByRowId(updatedMeta)
      setFeedKgMetaByRowId(updatedMeta)
    }

    persistRowsOrPatchEverywhere({
      context,
      rowId: id,
      nextRows,
      patch: { [col]: value } as Partial<BiomassDataRow>
    })

    if (numericNotification) {
      dismissNumericNotification()
    }

    return true
  }

  // Export current data as CSV using the same derivations the Database screen
  // renders. Resolves findings #14 (id/lotId/unmappedLot moved to the admin
  // tail) and #15 (derived dailyMortality from MORT-12 events and derived
  // fishStock from end-of-day stock helper instead of the blank stored cell).
  // The pure projection lives in `productionCsvExport.ts` so the contract is
  // unit-tested without mounting the screen.
  function exportCSV() {
    if (!data || data.length === 0) return
    const csv = buildProductionExportCsv({
      rows: data,
      columns: displayColumns,
      mortalityRollupByRowId,
      displayedEndOfDayStockByRowId,
      dailyMortalityRateByRowId
    })
    downloadTextBlob({
      text: csv,
      filename: `production-data-${Date.now()}.csv`,
      mimeType: 'text/csv;charset=utf-8;'
    })
  }

  // v2.2 T3-C-04: the inline CSV import path (importCSV / importCsvText and its lot
  // reconciliation helpers) and the `openImportWorkspace` launcher previously lived
  // here. The launcher moved to Biomass (`openProductionDatatableImport`) — the Tier A
  // surface that owns the write path — matching Water / Feed / Mortality. The inline
  // helpers were vestigial since v2.1 finding #39 (they bypassed parser padding, safe-
  // merge, locale decimals, and (lot, day) dedup warnings — all v2.1 hardening only
  // present in the Import Workspace path). The `productionDatatable` importer target
  // and its Import Workspace review-notice landing on `/database` (see
  // `parseImportWorkspaceReviewRouteState` above) are unchanged.

  const selectedFarmLabel = useMemo(() => {
    if (!farmId) return t('datatable.filter.allFacilities')
    const selected = availableFarms.find((farm: any) => String(farm.id ?? farm.name ?? '') === String(farmId))
    return selected?.name ?? String(farmId)
  }, [availableFarms, farmId, t])

  const selectedProductionUnitLabel = useMemo(() => {
    if (!productionUnitId) return t('datatable.filter.allProductionUnitsShort')
    const selected = availableProductionUnits.find((pu: any) => String(pu.id ?? pu.name ?? '') === String(productionUnitId))
    return selected?.name ?? String(productionUnitId)
  }, [availableProductionUnits, productionUnitId, t])

  const leadText = useMemo(() => {
    if (canEditProductionRecords) return t('datatable.lead.editable')
    const roleKey = `role.${principal?.role ?? 'viewer'}`
    const roleLabel = t(roleKey as any)
    return `${t('datatable.lead.readonlyPrefix')} (${roleLabel} token). ${t('datatable.lead.readonlySuffix')}`
  }, [canEditProductionRecords, principal?.role, t])

  const productionSyncAttentionMessage = useMemo(() => {
    if (!productionSyncStatus) return null

    if (productionSyncStatus.blockedCount > 0) {
      if (productionSyncStatus.errorMessage) {
        return t('datatable.sync.attentionBlockedWithReason', {
          count: productionSyncStatus.blockedCount,
          reason: productionSyncStatus.errorMessage
        })
      }

      return t('datatable.sync.attentionBlocked', { count: productionSyncStatus.blockedCount })
    }

    if (productionSyncStatus.syncState === 'offline') {
      return t('datatable.sync.attentionOffline')
    }

    if (productionSyncStatus.syncState === 'pending') {
      return t('datatable.sync.attentionPending')
    }

    return null
  }, [productionSyncStatus, t])

  const productionSyncLastResultSummary = useMemo(() => {
    if (!productionSyncLastOutcome) return null

    return {
      resultLabel: t(productionSyncLastOutcome.resultKey, productionSyncLastOutcome.resultParams),
      attemptedAtLabel: formatSyncAttemptedAtLabel(productionSyncLastOutcome.attemptedAtIso)
    }
  }, [productionSyncLastOutcome, t])

  const handleRetryProductionSync = async (): Promise<void> => {
    if (!selectedProductionContext) return

    if (currentProductionRowIds.length === 0) {
      const syncedStatus: EntityBatchSyncStatus = {
        syncState: 'synced',
        blockedCount: 0,
        errorMessage: null,
        blockers: []
      }
      setProductionSyncStatus(syncedStatus)
      const outcome = deriveDatatableSyncOutcome({ status: syncedStatus, skippedNotFoundCount: 0 })
      setProductionSyncLastOutcome({ ...outcome, attemptedAtIso: new Date().toISOString() })
      return
    }

    setRetryingProductionSync(true)

    try {
      const nextStatus = await retryProductionUnitTableRowSyncStatus(currentProductionRowIds)
      setProductionSyncStatus(nextStatus)

      const outcome = deriveDatatableSyncOutcome({
        status: nextStatus,
        skippedNotFoundCount: 0
      })
      setProductionSyncLastOutcome({
        ...outcome,
        attemptedAtIso: new Date().toISOString()
      })

      const refreshedRows = await refreshProductionUnitTableFromApi(selectedProductionContext)
      const { sanitized } = sanitizeNumericRows(refreshedRows, numericRules)
      setData(sanitized)
      // v2.1 finding #57: drain peer MortalityLog events on explicit Retry
      // so the post-retry render shows the freshest dailyMortality cells.
      await refreshCurrentMortalityLogs({ forceRemote: true })
    } catch {
      setProductionSyncLastOutcome({
        resultKey: 'datatable.sync.retryFailed',
        attemptedAtIso: new Date().toISOString()
      })
    } finally {
      setRetryingProductionSync(false)
    }
  }

  const handleRefreshProductionSync = async (): Promise<void> => {
    if (!selectedProductionContext) return

    setRefreshingProductionSync(true)

    try {
      const refreshedRows = await refreshProductionUnitTableFromApi(selectedProductionContext)
      const { sanitized } = sanitizeNumericRows(refreshedRows, numericRules)
      setData(sanitized)
      // v2.1 finding #57 (Phase 4 Session 2 Block A4): the explicit "Actualizar
      // registros" button must also pull MortalityLog from server, not only
      // ProductionRecords. Without this, a peer-saved mortality event never
      // surfaces on this device even with an explicit operator refresh tap.
      await refreshCurrentMortalityLogs({ forceRemote: true })

      const nextStatus = await refreshCurrentProductionSyncStatus()
      const outcome = deriveDatatableSyncOutcome({
        status: nextStatus ?? {
          syncState: 'synced',
          blockedCount: 0,
          errorMessage: null,
          blockers: []
        },
        skippedNotFoundCount: 0
      })

      setProductionSyncLastOutcome({
        ...outcome,
        attemptedAtIso: new Date().toISOString()
      })
    } catch {
      setProductionSyncLastOutcome({
        resultKey: 'datatable.sync.refreshFailed',
        attemptedAtIso: new Date().toISOString()
      })
    } finally {
      setRefreshingProductionSync(false)
    }
  }

  const toEditableString = (value: unknown) => {
    if (typeof value === 'string') return value
    if (typeof value === 'number') return value.toString()
    return ''
  }

  const isEditing = (rowId: string, col: string) => {
    return editingCell?.id === rowId && editingCell?.col === col
  }

  const commitEditAndClose = (rowId: string, col: string, nextValue: string) => {
    if (saveCell(rowId, col, nextValue)) {
      setEditingCell(null)
      return true
    }
    return false
  }

  const beginEditCell = (row: any, col: string) => {
    if (!canEditProductionRecords) {
      showNumericNotification(t('datatable.readonly.cannotEditRecords'))
      return
    }
    if (deletedLotRowIds.has(String(row.id))) {
      showNumericNotification(t('datatable.deletedLot.readOnly'))
      return
    }
    if (movedLotDestinationByRowId.has(String(row.id))) {
      showNumericNotification(t('datatable.movedLot.readOnly'))
      return
    }

    // v2.2 T3-A-07b (2026-07-06): the ProductionRecord biology cache
    // columns (`weightGrams`, `dailyMortality`, `fishStock`) are retired.
    // These cells now ALWAYS show overlay-derived values (from Tier A
    // event streams) — any inline edit would silently vanish because the
    // Prisma mapper drops the field from the payload. Block the edit
    // regardless of whether events exist; direct operators to the
    // dedicated log surface (Sampling for weight/mortality;
    // Conteo/Estimación for fishStock — the latter surface lands in
    // T3-C-01). Retains the finding #8 / #69 read-only affordance and
    // extends it to fishStock. See
    // [docs/architecture-concerns.md](../../docs/architecture-concerns.md)
    // invariants I-1 + I-3.
    if (col === 'dailyMortality') {
      showNumericNotification(t('datatable.mortality.readOnlyEditAttempt'))
      return
    }
    if (col === DAILY_MORTALITY_RATE_COL) {
      showNumericNotification(t('datatable.mortalityRate.readOnly'))
      return
    }
    if (col === 'weight') {
      showNumericNotification(t('datatable.weight.readOnlyEditAttempt'))
      return
    }
    if (col === 'fishStock') {
      showNumericNotification(t('datatable.mortality.readOnlyEditAttempt'))
      return
    }

    // v2.2 findings.md #23: `harvestKg`/`harvestCount` are equally
    // overlay-derived (summed from HarvestLog by (date, lot) —
    // harvestLogBridge.ts's overlayHarvestOnProductionRows) but were never
    // added to this blocklist alongside weight/dailyMortality/fishStock.
    if (col === 'harvestKg' || col === 'harvestCount') {
      showNumericNotification(t('datatable.harvest.readOnlyEditAttempt'))
      return
    }

    // v2.2 findings.md #23: a blind patch of `date` on this row's own `id`
    // does not move (or even check for a collision with) the Tier A events
    // still linked to it by day — the exact "orphans the events still
    // pointing at it" risk the `lot` guard below already exists to prevent,
    // just never extended to `date`. Blocked unconditionally, matching the
    // weight/dailyMortality/fishStock precedent above (not conditional on
    // whether linked events currently exist, for the same consistency
    // reasoning that precedent already established).
    //
    // Follow-up: this branch is unreachable for the actual `date` cell —
    // it renders via `renderBodyCell`'s `isDateCol` branch, which never
    // calls `beginEditCell` (its `DateTimeLocalInput` fires `saveCell`
    // directly). Kept as defense-in-depth for any other caller; `saveCell`
    // now carries the same guard as the enforcement point that matters.
    if (col === 'date') {
      showNumericNotification(t('datatable.date.readOnlyEditAttempt'))
      return
    }

    // U4 (docs/lot-lifecycle-vs-concerns-audit.md §3, extends I-3): clicking
    // the `lot` cell no longer opens the generic reassignment editor. The
    // only legitimate way to change this cell from the rollup screen is the
    // unmapped-label recovery flow, whose buttons
    // (`startMappingUnmappedLotToExisting`) set
    // `editingCell`/patch the row directly and never call `beginEditCell`.
    if (col === 'lot') {
      showNumericNotification(t('datatable.error.lotEditOutsideRecovery'))
      return
    }

    setEditingCell({ id: row.id, col })

    setEditingValue(toEditableString(row[col] ?? ''))
  }

  const renderFeedKgSourceNote = (rowId: string, value: string) => {
    const note = getFeedKgSourceNote(rowId)
    if (!note) return null
    if (!value.trim()) return null
    const style: React.CSSProperties = { fontSize: '11px', fontStyle: 'italic', opacity: 0.75, marginTop: '2px' }

    return (
      <div style={style} title={note.title}>
        {note.label}
      </div>
    )
  }

  const renderLotEditor = (row: any) => {
    if (!selectedFarmAndUnit) return null

    const unmappedLot = toEditableString(row.unmappedLot).trim()

    if (availableLots.length > 0) {
      return (
        <select
          autoFocus
          value={editingValue}
          onChange={(e) => {
            const val = e.target.value
            setEditingValue(val)
            commitEditAndClose(String(row.id), 'lot', val)
          }}
          onBlur={() => {
            setEditingCell(null)
          }}
          style={{ ...styles.input, padding: '4px' }}
        >
          <option value="">{t('datatable.option.none')}</option>
          {availableLots.map((lot) => (
            <option
              key={lot.id}
              value={lot.id}
              disabled={!isLotActiveForInteraction(lot.stage)}
            >
              {formatLotPickerLabel(lot)}
            </option>
          ))}
        </select>
      )
    }

    return (
      <div>
        <select autoFocus value="" disabled style={{ ...styles.input, padding: '4px' }}>
          <option value="">{t('datatable.lot.noneYet')}</option>
        </select>
        {unmappedLot && (
          <div style={{ fontSize: '11px', color: '#dc3545', marginTop: '4px' }}>
            {t('datatable.lot.unmappedPrefix')} {unmappedLot}
          </div>
        )}
        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>{t('datatable.lot.createInLotsHelp')}</div>
        {unmappedLot && (
          <div style={{ marginTop: '6px' }}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                if (!canEditProductionRecords) {
                  showNumericNotification(t('datatable.readonly.cannotEditRecords'))
                  return
                }
                const rowId = String(row.id)
                const context = getEditContext()
                const patch = { lot: '', lotId: '', unmappedLot: '' }
                const nextRows = patchRowsById(data, rowId, patch)
                setData(nextRows)
                clearLotAutoClearedId(rowId)
                persistRowsOrPatchEverywhere({ context, rowId, nextRows, patch })
                setEditingCell(null)
              }}
              style={{
                padding: '4px 8px',
                borderRadius: 6,
                border: '1px solid #ccc',
                background: '#fff',
                cursor: 'pointer',
                fontSize: 12
              }}
              disabled={!canEditProductionRecords}
            >
              {t('datatable.lot.ignoreUnmapped')}
            </button>
          </div>
        )}
        <div style={{ marginTop: '6px' }}>
          <GradientButton
            variant="primary"
            size="sm"
            onClick={() => {
              setEditingCell(null)
              navigate('/lots')
            }}
          >
            {t('datatable.lot.goToLots')}
          </GradientButton>
        </div>
      </div>
    )
  }

  const renderStageEditor = (row: any) => (
    <select
      autoFocus
      value={editingValue}
      onChange={(e) => {
        const val = e.target.value
        setEditingValue(val)
        commitEditAndClose(String(row.id), 'stage', val)
      }}
      onBlur={() => {
        setEditingCell(null)
      }}
      style={{ ...styles.input, padding: '4px' }}
    >
      <option value="Active">{t('datatable.stage.active')}</option>
      <option value="Inactive">{t('datatable.stage.inactive')}</option>
    </select>
  )

  const renderHealthEditor = (row: any) => (
    <select
      autoFocus
      value={editingValue}
      onChange={(e) => {
        const val = e.target.value
        setEditingValue(val)
        commitEditAndClose(String(row.id), 'health', val)
      }}
      onBlur={() => {
        setEditingCell(null)
      }}
      style={{ ...styles.input, padding: '4px' }}
    >
      <option value="Good">{t('datatable.health.good')}</option>
      <option value="Issue">{t('datatable.health.issue')}</option>
      <option value="Emergency">{t('datatable.health.emergency')}</option>
    </select>
  )

  const renderMortEditor = (row: any) => (
    <select
      autoFocus
      value={editingValue}
      onChange={(e) => {
        const val = e.target.value
        setEditingValue(val)
        commitEditAndClose(String(row.id), 'mort', val)
      }}
      onBlur={() => {
        setEditingCell(null)
      }}
      style={{ ...styles.input, padding: '4px' }}
    >
      <option value="">{t('datatable.option.none')}</option>
      <option value="Vaccine">{t('datatable.treatment.vaccine')}</option>
      <option value="Antibiotic">{t('datatable.treatment.antibiotic')}</option>
      <option value="Disinfectant">{t('datatable.treatment.disinfectant')}</option>
      <option value="Salt">{t('datatable.treatment.salt')}</option>
      <option value="Other">{t('datatable.treatment.other')}</option>
    </select>
  )

  const renderDefaultEditor = (row: any, col: string, isAutoClearedLot: boolean) => {
    const rowId = String(row.id)
    const showAutoClearedHint =
      col === 'lot' && selectedFarmAndUnit && !lotsLoading && availableLots.length > 0 && isAutoClearedLot
    const currentValue = toEditableString(editingValue)
    const feedKgValue = col === 'feedKg' ? toEditableString(row[col] ?? '') : ''
    const rowProjection = col === 'dailyMortality'
      ? resolveMortalityProjectionForRow({
        row: {
          date: String(row.date ?? ''),
          lot: String(row.lot ?? ''),
          lotId: String(row.lotId ?? ''),
          weight: lastKnownWeightByRowId.get(rowId) ?? row.weight,
          fishStock: row.fishStock
        },
        availableLots,
        latestTemperatureByLotWeek: latestProjectionTemperatureByLotWeek,
        recentTemperatureIndex: recentProjectionTemperatureIndex,
        speciesKey,
        mortalityMatrix: mortalityMatrixData
      })
      : null
    const hasProjectionInputs = col === 'dailyMortality'
      && Boolean(String(row.date ?? '').trim())
      && Boolean(String(row.lotId ?? row.lot ?? '').trim())
      && Number.parseFloat(String(lastKnownWeightByRowId.get(rowId) ?? row.weight ?? '')) > 0
      && Number.parseFloat(String(row.fishStock ?? '')) > 0

    return (
      <>
        <input
          autoFocus
          type={col === 'dailyMortality' ? 'number' : undefined}
          min={col === 'dailyMortality' ? 0 : undefined}
          step={col === 'dailyMortality' ? 1 : undefined}
          value={currentValue}
          disabled={!canEditProductionRecords}
          onChange={(e) => setEditingValue(e.target.value)}
          onBlur={() => {
            commitEditAndClose(rowId, col, currentValue)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commitEditAndClose(rowId, col, currentValue)
            }
            if (e.key === 'Escape') {
              setEditingCell(null)
            }
          }}
          style={{
            ...styles.input,
            borderColor: '#ccc'
          }}
          placeholder={undefined}
        />

        {col === 'feedKg' && renderFeedKgSourceNote(rowId, feedKgValue)}

        {col === 'dailyMortality' && rowProjection && (
          <div style={{ fontSize: '11px', color: '#0f766e', marginTop: '4px', lineHeight: 1.45 }}>
            {t('datatable.mortalityGuide.recommendation', {
              expectedMortality: rowProjection.expectedMortalityRate.toFixed(2),
              temperature: rowProjection.temperatureC.toFixed(1),
              projectedSurvival: rowProjection.projectedSurvivalRate.toFixed(2),
              projectedStock: rowProjection.projectedStock.toLocaleString(undefined, { maximumFractionDigits: 0 }),
              projectedBiomass: rowProjection.projectedBiomassKg.toFixed(1)
            })}
          </div>
        )}

        {col === 'dailyMortality' && !rowProjection && hasProjectionInputs && (
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', lineHeight: 1.45 }}>
            {t('datatable.mortalityGuide.noTemperature')}
          </div>
        )}

        {col === 'dailyMortality' && !rowProjection && !hasProjectionInputs && (
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', lineHeight: 1.45 }}>
            {t('datatable.mortalityGuide.missingInputs')}
          </div>
        )}

        {showAutoClearedHint && (
          <div style={{ fontSize: '11px', color: '#dc3545', marginTop: '4px' }}>{t('datatable.lot.clearedNotInList')}</div>
        )}
      </>
    )
  }

  const renderEditorContent = (row: any, col: string, isAutoClearedLot: boolean) => {
    if (col === 'lot' && selectedFarmAndUnit) return renderLotEditor(row)
    if (col === 'stage') return renderStageEditor(row)
    if (col === 'health') return renderHealthEditor(row)
    if (col === 'mort') return renderMortEditor(row)
    return renderDefaultEditor(row, col, isAutoClearedLot)
  }

  const startMappingUnmappedLotToExisting = (e: React.MouseEvent, row: any, col: string) => {
    e.stopPropagation()
    if (!canEditProductionRecords) {
      showNumericNotification(t('datatable.readonly.cannotEditRecords'))
      return
    }
    setEditingCell({ id: row.id, col })
    setEditingValue('')
  }

  const clearUnmappedLot = (e: React.MouseEvent, row: any) => {
    e.stopPropagation()
    if (!canEditProductionRecords) {
      showNumericNotification(t('datatable.readonly.cannotEditRecords'))
      return
    }

    const rowId = String(row.id)
    const context = getEditContext()
    const patch = { lot: '', lotId: '', unmappedLot: '' }
    const nextRows = patchRowsById(data, rowId, patch)
    setData(nextRows)
    clearLotAutoClearedId(rowId)
    persistRowsOrPatchEverywhere({ context, rowId, nextRows, patch })
  }

  const renderLotDisplay = (row: any, col: string) => {
    if (!selectedFarmAndUnit) return null

    const lotUiState = resolveUnmappedLotUiState({
      unmappedLotLabel: row.unmappedLot,
      availableLots
    })
    if (!lotUiState.hasUnmappedLot) return null

    const { unmappedLot, isAmbiguousLot, canCreateNewLot, canMapToExisting } = lotUiState

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ color: '#dc3545', fontSize: '12px', fontWeight: 600 }}>
          {isAmbiguousLot ? t('datatable.lot.ambiguousPrefix') : t('datatable.lot.unmappedPrefix')} {unmappedLot}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canMapToExisting && (
            <button
              type="button"
              onClick={(e) => startMappingUnmappedLotToExisting(e, row, col)}
              style={{
                padding: '4px 8px',
                borderRadius: 6,
                border: '1px solid #ccc',
                background: '#fff',
                cursor: 'pointer',
                fontSize: 12
              }}
              disabled={lotsLoading || !canEditProductionRecords}
            >
              {t('datatable.lot.mapToExisting')}
            </button>
          )}
          {canCreateNewLot && (
            // Findings #199: lots are created only in Lotes, with their stocking date.
            <span style={{ fontSize: 11, color: '#64748b', alignSelf: 'center' }}>{t('datatable.lot.createInLotsHint')}</span>
          )}
          <button
            type="button"
            onClick={(e) => clearUnmappedLot(e, row)}
            style={{
              padding: '4px 8px',
              borderRadius: 6,
              border: '1px solid #ccc',
              background: '#fff',
              cursor: 'pointer',
              fontSize: 12
            }}
            disabled={!canEditProductionRecords}
          >
            {t('datatable.lot.ignoreUnmapped')}
          </button>
        </div>
      </div>
    )
  }

  // Track A finding 3.1: read-only "daily mortality %" cell for the synthetic
  // DAILY_MORTALITY_RATE_COL column. `null` rate (first day of a lot, blank
  // count, or unknown start-of-day stock) renders an em dash.
  //
  // Traffic-light dot: compares the observed rate against the same
  // matrix-projected expected rate already shown as a hint while editing
  // this cell (`resolveMortalityProjectionForRow`, reused as-is — same
  // weight/temperature/species inputs, same one-day-ahead call shape).
  // Only rendered when a projection is actually computable (real
  // temperature data for that lot/week); otherwise there's nothing
  // meaningful to compare against and the dot is omitted rather than
  // guessed at. Clicking it opens a pre-filled task for that lot.
  const renderDailyMortalityRateCell = (row: any) => {
    const rowId = String(row.id)
    const rate = dailyMortalityRateByRowId.get(rowId)
    if (!rate || rate.ratePct === null) {
      return <span style={{ color: '#94a3b8' }}>—</span>
    }

    // resolveMortalityProjectionForRow requires a positive fishStock to
    // resolve at all, but only actually uses its numeric value for the
    // projected-stock/biomass fields, never for expectedMortalityRate
    // (weight + temperature only) — so falling back to the same derived
    // stock the "Stock de peces (derivado)" column already shows is safe.
    // Findings #138: the weight is the lot's last known one and the
    // temperature its latest of the past 7 days; when something is still
    // missing (or the lot is eggs) the dot is grey and says what.
    const derivedStock = displayedEndOfDayStockByRowId.get(rowId)?.numericValue
    const explicitLotId = String(row.lotId ?? '').trim()
    const rowLotLabel = String(row.lot ?? '').trim()
    const rowLot = explicitLotId
      ? availableLots.find((lot) => lot.id === explicitLotId)
      : (() => {
        const match = rowLotLabel ? findAvailableLotMatch(rowLotLabel, availableLots) : null
        return match?.status === 'matched' ? availableLots.find((lot) => lot.id === String(match.match.id)) : undefined
      })()
    const rowDay = farmDayKey(row.date, farmTimeZone(farmId)) ?? String(row.date ?? '').slice(0, 10)
    const stageLot = asStageLot(rowLot)
    const rowStage = stageLot ? lifeStageOn(stageLot, rowDay) : null
    const criticalDay = stageLot ? criticalDayOn(stageLot, rowDay, rowLot ? firstTransferByLotId.get(rowLot.id) : null) : null
    const dot = resolveMortalityDot({
      observedRatePct: rate.ratePct,
      ...(rowStage || criticalDay ? { earlyStage: { stage: rowStage, criticalDay, defaults: hatcheryDefaults } } : {}),
      row: {
        date: String(row.date ?? ''),
        lot: rowLotLabel,
        lotId: explicitLotId,
        weight: lastKnownWeightByRowId.get(rowId) ?? row.weight,
        fishStock: row.fishStock || derivedStock
      },
      availableLots,
      latestTemperatureByLotWeek: latestProjectionTemperatureByLotWeek,
      recentTemperatureIndex: recentProjectionTemperatureIndex,
      speciesKey,
      mortalityMatrix: mortalityMatrixData
    })
    const severityColor = dot.kind === 'missing'
      ? '#cbd5e1'
      : dot.severity === 'green' ? '#16a34a' : dot.severity === 'yellow' ? '#eab308' : '#dc2626'
    const dotLabel = dot.kind === 'severity'
      ? t(`datatable.mortalityRate.severity.${dot.severity}`, { expected: dot.expectedRatePct.toFixed(2) })
      : dot.kind === 'stageReference'
        ? t(`datatable.mortalityRate.stage.${dot.severity}`, {
          day: dot.criticalDay
            ? t('datatable.mortalityRate.stage.critical', { moment: t(`datatable.mortalityRate.critical.${dot.criticalDay}`) })
            : t(`datatable.mortalityRate.stage.${dot.reference}`),
          green: formatPct(dot.greenMaxPct),
          red: formatPct(dot.redFromPct)
        })
        : t(`datatable.mortalityRate.missing.${dot.reason}`)

    const handleCreateTask = (event: React.MouseEvent) => {
      event.stopPropagation()
      // Mirrors resolveMortalityProjectionForRow's own lotId resolution
      // (row.lotId first, label lookup as fallback) — many rows only carry
      // the lot label, not the id, and the task's context needs the real
      // id to filter/tag correctly, not just display the label.
      const explicitLotId = String(row.lotId ?? '').trim()
      const lotLabel = String(row.lot ?? '').trim()
      const lotMatch = !explicitLotId && lotLabel ? findAvailableLotMatch(lotLabel, availableLots) : null
      const lotId = explicitLotId || (lotMatch?.status === 'matched' ? lotMatch.match.id : '')
      if (lotId) persistActiveContextSelection({ lotId })
      const dateLabel = String(row.date ?? '').slice(0, 10)
      const title = t('datatable.mortalityRate.taskTitle', { lot: lotLabel, date: dateLabel })
      navigate(`/tasks?focus=create&title=${encodeURIComponent(title)}`)
    }

    return (
      <span
        title={t('datatable.mortalityRate.cellTitle', {
          count: String(rate.mortalityCount ?? 0),
          stock: (rate.startOfDayStock ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })
        })}
        style={{ fontWeight: 600, color: '#0f766e' }}
      >
        {t('datatable.mortalityRate.cell', { pct: rate.ratePct.toFixed(2) })}
        <button
            type="button"
            data-testid={`datatable-mortality-dot-${rowId}`}
            data-dot={dot.kind === 'missing' ? dot.reason : dot.severity}
            onClick={handleCreateTask}
            title={dotLabel}
            aria-label={dotLabel}
            style={{
              display: 'inline-block',
              width: 9,
              height: 9,
              borderRadius: '50%',
              marginLeft: 7,
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              backgroundColor: severityColor,
              verticalAlign: 'middle'
            }}
          />
      </span>
    )
  }

  const renderDisplayContent = (row: any, col: string) => {
    const rowId = String(row.id)

    if (col === DAILY_MORTALITY_RATE_COL) {
      return renderDailyMortalityRateCell(row)
    }

    if (col === 'feedKg') {
      const displayValue = toEditableString(row[col] ?? '')
      const note = getFeedKgSourceNote(rowId)
      if (note && displayValue.trim()) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div>{displayValue}</div>
            {renderFeedKgSourceNote(rowId, displayValue)}
          </div>
        )
      }
      return displayValue
    }

    if (col === 'lot' && deletedLotRowIds.has(rowId)) {
      return (
        <span title={t('datatable.deletedLot.title')}>
          {toEditableString(row.lot ?? '') || toEditableString(row.unmappedLot ?? '')}
          {' '}
          <span style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>
            ({t('datatable.deletedLot.tag')})
          </span>
        </span>
      )
    }

    if (col === 'lot' && movedLotDestinationByRowId.has(rowId) && toEditableString(row.lot ?? '')) {
      const destination = movedLotDestinationByRowId.get(rowId)
      return (
        <span title={t('datatable.movedLot.title')}>
          {toEditableString(row.lot ?? '')}
          {' '}
          <span style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>
            ({destination ? t('datatable.movedLot.tagTo', { unit: destination }) : t('datatable.movedLot.tag')})
          </span>
        </span>
      )
    }

    if (col === 'lot') {
      const lotDisplay = renderLotDisplay(row, col)
      if (lotDisplay) return lotDisplay
    }

    if (col === 'dailyMortality') {
      const rollup = mortalityRollupByRowId.get(rowId)
      if (rollup) {
        const countLabel = String(rollup.eventCount)
        return (
          <span
            title={t('datatable.mortality.derivedFromEventsTitle', { count: countLabel })}
            style={{ color: '#0f766e', fontStyle: 'italic' }}
          >
            {rollup.totalDailyMortality}
            {' '}
            <button
              type="button"
              data-testid="mortality-events-badge"
              onClick={(e) => {
                e.stopPropagation()
                setMortalityEventsPopoverRowId(rowId)
              }}
              title={t('datatable.mortality.events.badgeAction', { count: countLabel })}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                fontSize: '11px',
                color: '#0f766e',
                fontStyle: 'italic',
                cursor: 'pointer',
                textDecoration: 'underline dotted',
                textUnderlineOffset: 2
              }}
            >
              ({t('datatable.mortality.derivedFromEventsLabel', { count: countLabel })})
            </button>
          </span>
        )
      }
    }

    if (col === 'weight') {
      // v2.1 finding #35 → v2.1 finding #60 (WSL-06): when multiple
      // `WeightSampleLog` events land on the same (lot, day), the displayed
      // weight is the arithmetic mean (composed via
      // `overlayWeightSamplesOnProductionRows` upstream) and the badge opens
      // SamplingEventsPopover so operators can audit the server-backed inputs.
      const eventCount = weightSampleRollupByRowId.get(rowId)?.eventCount ?? 0
      const storedWeight = toEditableString(row[col] ?? '')
      if (eventCount >= 2) {
        const countLabel = String(eventCount)
        return (
          <span
            title={t('datatable.sampling.averagedFromEventsTitle', { count: countLabel })}
            style={{ color: '#0f766e', fontStyle: 'italic' }}
          >
            {storedWeight || '—'}
            {' '}
            <button
              type="button"
              data-testid="sampling-events-badge"
              onClick={(e) => {
                e.stopPropagation()
                setSamplingEventsPopoverRowId(rowId)
              }}
              title={t('datatable.sampling.events.badgeAction', { count: countLabel })}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                fontSize: '11px',
                color: '#0f766e',
                fontStyle: 'italic',
                cursor: 'pointer',
                textDecoration: 'underline dotted',
                textUnderlineOffset: 2
              }}
            >
              ({t('datatable.sampling.averagedFromEventsLabel', { count: countLabel })})
            </button>
          </span>
        )
      }
      if (storedWeight) return storedWeight
      // Findings #141: a day without a sample shows the lot's last weight,
      // greyed like the derived stock (display only, never stored).
      const lastWeight = lastKnownWeightByRowId.get(rowId)
      if (lastWeight === undefined) return ''
      return (
        <span
          data-testid={`datatable-weight-last-known-${rowId}`}
          title={t('datatable.weight.lastKnownTitle')}
          style={{ color: '#64748b', fontStyle: 'italic' }}
        >
          {lastWeight}{' '}
          <span style={{ fontSize: '11px' }}>({t('datatable.weight.lastKnownLabel')})</span>
        </span>
      )
    }

    if (col === 'fishStock') {
      const stored = toEditableString(row[col] ?? '')
      if (!stored.trim()) {
        const derived = displayedEndOfDayStockByRowId.get(rowId)
        if (derived && derived.source === 'derived') {
          return (
            <span title={t('datatable.fishStock.derivedTitle')} style={{ color: '#64748b', fontStyle: 'italic' }}>
              {derived.value}
              {' '}
              <span style={{ fontSize: '11px' }}>({t('datatable.fishStock.derivedLabel')})</span>
            </span>
          )
        }
      }
      return stored || ''
    }

    return toEditableString(row[col] ?? '') || ''
  }

  const renderBodyCell = (row: any, col: string) => {
    const isDateCol = col === 'date'
    const isInvalidDate = isDateCol && invalidDates.has(row.id)
    const isLotCol = col === 'lot'
    const isAutoClearedLot = isLotCol && lotAutoClearedIds.has(row.id)
    const cellStyle: any = {
      ...styles.cell,
      backgroundColor: isInvalidDate || isAutoClearedLot ? '#fee' : (styles.cell as any).background
    }
    if (isDateCol) {
      Object.assign(cellStyle, {
        minWidth: 220,
        maxWidth: 280,
        whiteSpace: 'normal' as const,
        overflow: 'visible',
        textOverflow: 'unset',
        wordBreak: 'break-all' as const
      })
    }

    if (isDateCol) {
      const raw = String(row[col] ?? '')
      const borderColor = isInvalidDate ? '#dc3545' : '#ccc'
      const sameDaySummary = sameDayRowsByRowId.get(String(row.id))
      const sameDayCount = sameDaySummary ? sameDaySummary.count : 0
      const sameDayLabel = String(sameDayCount)
      return (
        <td key={col} style={cellStyle}>
          <DateTimeLocalInput
            value={raw}
            timeZone={farmTimeZone(farmId)}
            disabled={!canEditProductionRecords || isReadOnlyLotRow(String(row.id))}
            onChangeValue={(nextValue) => {
              saveCell(String(row.id), col, nextValue)
            }}
            invalid={isInvalidDate}
            pickerOnly
            onRequestReveal={scrollDateInputIntoView}
            style={{
              ...styles.input,
              borderColor
            }}
            placeholder={t('datatable.date.placeholder')}
          />
          {isInvalidDate && (
            <div style={{ fontSize: '11px', color: '#dc3545', marginTop: '4px' }}>{t('datatable.date.invalidHint')}</div>
          )}
          {sameDayCount > 1 && (
            <div style={{ marginTop: 4 }}>
              <button
                type="button"
                data-testid="same-day-rows-badge"
                onClick={(e) => {
                  e.stopPropagation()
                  setSameDayRowsPopoverRowId(String(row.id))
                }}
                title={t('datatable.sameDayRows.badgeTitle', { count: sameDayLabel })}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  fontSize: '11px',
                  color: '#0f766e',
                  fontStyle: 'italic',
                  cursor: 'pointer',
                  textDecoration: 'underline dotted',
                  textUnderlineOffset: 2
                }}
              >
                ({t('datatable.sameDayRows.badge', { count: sameDayLabel })})
              </button>
            </div>
          )}
        </td>
      )
    }

    const rowId = String(row.id)
    const content = isEditing(rowId, col)
      ? renderEditorContent(row, col, isAutoClearedLot)
      : renderDisplayContent(row, col)
    return (
      <td key={col} style={cellStyle} onClick={() => beginEditCell(row, col)}>
        {content}
      </td>
    )
  }

  // v2.1 finding #36 — one-shot legacy duplicate merge migration. Operator
  // confirms in the modal; we then sequentially: rebind linked
  // MortalityLog.metadata.productionRowId from each absorbed row to the
  // kept row, upsert the merged row, delete the absorbed rows (Dexie +
  // outbox), and persist the seeded sampling aggregate so the popover
  // surfaces evidence. Errors halt the migration and surface in the modal.
  const runLegacyDuplicateMigration = async () => {
    if (!selectedProductionContext) return
    if (legacyDuplicateMergePlans.length === 0) {
      setLegacyDuplicateMigrationOpen(false)
      return
    }

    setLegacyDuplicateMigrationRunning(true)
    setLegacyDuplicateMigrationError(null)

    const context = selectedProductionContext
    // v2.1 finding #60 (WSL-08): the sidecar aggregate write path retired.
    // The migration used to fold `plan.nextSamplingAggregate` into a
    // localStorage-backed `samplingAggregateByRowId` and re-persist it. Since
    // the merged weight is already committed via `upsertProductionUnitTableRowAndSync`
    // and per-event history is server-backed by `WeightSampleLog`, the local
    // sidecar update is a no-op. `plan.nextSamplingAggregate` is retained
    // in the plan output only for the unit tests that pin the invariant.
    const reboundMetadataByLogId = new Map<string, Record<string, unknown>>()
    const reboundFishStockByLogId = new Map<string, { productionRowId: string; metadata: Record<string, unknown> }>()
    const absorbedRowIds: string[] = []
    const mergedRowsById = new Map<string, BiomassDataRow>()

    try {
      for (const plan of legacyDuplicateMergePlans) {
        for (const rebind of plan.logRebinds) {
          await mortalityLogApi.update(rebind.logId, { metadata: rebind.nextMetadata })
          reboundMetadataByLogId.set(rebind.logId, rebind.nextMetadata)
        }

        // v2.2 finding #2 (2026-07-13): rebind absorbed-row-bound FishStockLog
        // events onto the kept row BEFORE the absorbed rows are deleted, so
        // the merged row inherits per-event provenance instead of showing a
        // blank fish stock. Pre-fix behaviour deleted these events outright
        // via `fishStockLogApi.delete` inside the absorbed-rows loop below,
        // leaving the fish-stock overlay empty for the merged (day, lot).
        // Mirrors the MortalityLog `plan.logRebinds` pattern above.
        for (const rebind of plan.fishStockLogRebinds) {
          await fishStockLogApi.update(rebind.logId, {
            productionRowId: rebind.nextProductionRowId,
            metadata: rebind.nextMetadata
          })
          reboundFishStockByLogId.set(rebind.logId, {
            productionRowId: rebind.nextProductionRowId,
            metadata: rebind.nextMetadata
          })
        }

        await upsertProductionUnitTableRowAndSync(context, plan.mergedRow)
        mergedRowsById.set(plan.mergedRow.id, plan.mergedRow)

        // v2.1 finding #67 site (a) (rehearsal Session 3, Block P2.2–P2.3,
        // 2026-07-03): persist the planner's synthesized sampling events via
        // `weightSampleLogApi.create`. Without this the WSL-08 rule-of-record
        // ("always seeds sampling events from the group rows themselves") is
        // satisfied at the planner layer (unit-tested) but silently dropped
        // at the applier — leaving the merged row with zero WSL evidence
        // server-side and an empty `SamplingEventsPopover` for the operator
        // trying to verify "did the merge preserve my data?". See
        // [docs/release/v2.1/findings.md](../../docs/release/v2.1/findings.md)
        // row #67.
        //
        // A1.1 Fix A: the payload is built by
        // `buildLegacyDuplicateMergeWeightSamplePayload` so the top-level
        // `lotId` column (read by `lotMatches`/`LotStoryService`) is written
        // alongside `metadata.lotId` from a single source. Previously only
        // metadata carried the lotId, so merged rows fell out of the lot
        // story once the lot was transferred.
        const createdMergedWeightSampleLogs: WeightSampleLog[] = []
        for (const event of plan.nextSamplingAggregate.events) {
          const createResult = await weightSampleLogApi.create(
            buildLegacyDuplicateMergeWeightSamplePayload({
              farmId: context.farmId,
              productionUnitId: context.productionUnitId,
              mergedRow: plan.mergedRow,
              event
            })
          )
          createdMergedWeightSampleLogs.push(createResult.data)
        }

        for (const absorbed of plan.absorbedRows) {
          // v2.1 finding #67 site (b): clear any WSL events linked to
          // absorbed rows before deleting them. Latent gap (absorbed rows
          // are legacy pre-cutover shapes usually without WSL companions),
          // but the invariant "delete of ProductionRecord must clear paired
          // WeightSampleLog rows" must hold on every code path. Mirrors #65.
          const linkedAbsorbedWsl = findAllLinkedWeightSampleLogsForRow(absorbed, weightSampleLogs)
          const deletedAbsorbedWslIds: string[] = []
          for (const wsl of linkedAbsorbedWsl) {
            await weightSampleLogApi.delete(wsl.id, { reason: t('datatable.duplicateMerge.auditReason') })
            deletedAbsorbedWslIds.push(wsl.id)
          }
          if (deletedAbsorbedWslIds.length > 0) {
            const removed = new Set(deletedAbsorbedWslIds)
            setWeightSampleLogs((prev) => prev.filter((log) => !removed.has(log.id)))
          }

          // v2.2 T3-A-07b (2026-07-06): mirror the WSL absorbed-row delete-
          // parity for `FishStockLog`. The ProductionRecord.fishStock cache
          // column retires in this card; without this parity, paired
          // FishStockLog events orphan on absorbed-row delete during
          // fuse-migration. Same #67 site (b) shape.
          //
          // v2.2 finding #2 (2026-07-13): the fuse path REBINDS these events
          // to the kept row via `plan.fishStockLogRebinds` at the top of this
          // `for (const plan of ...)` iteration instead of deleting them,
          // parallel to the MortalityLog rebind path. Day-scoped FSL events
          // (no `productionRowId`) are already correctly bound via the
          // (date, lot) overlay match on the kept row, so no delete action
          // is required here. The invariant I-2 delete-parity guarantee for
          // absorbed rows continues to hold for the plain-row-delete path in
          // `deleteRowById` (below) — only the fuse case rebinds.

          await deleteProductionUnitTableRowAndSync(context, absorbed.id)
          absorbedRowIds.push(absorbed.id)
          void dequeueProductionRow(absorbed.id)
        }

        if (createdMergedWeightSampleLogs.length > 0) {
          setWeightSampleLogs((prev) => [...createdMergedWeightSampleLogs, ...prev])
        }
      }

      setData((prev) => {
        const remaining = (prev as BiomassDataRow[]).filter((row) => !absorbedRowIds.includes(row.id))
        return remaining.map((row) => mergedRowsById.get(row.id) ?? row)
      })

      setMortalityLogs((prev) => prev.map((log) => {
        const next = reboundMetadataByLogId.get(log.id)
        if (!next) return log
        return { ...log, metadata: next }
      }))

      // v2.2 finding #2: mirror the mortality rebind state fold for
      // FishStockLog so the overlay's next render sees the merged row's
      // fish stock without waiting for a full list re-fetch.
      if (reboundFishStockByLogId.size > 0) {
        setFishStockLogs((prev) => prev.map((log) => {
          const rebind = reboundFishStockByLogId.get(log.id)
          if (!rebind) return log
          return { ...log, productionRowId: rebind.productionRowId, metadata: rebind.metadata }
        }))
      }

      setLegacyDuplicateMigrationOpen(false)
      const totalRebinds = legacyDuplicateMergePlans.reduce(
        (acc, plan) => acc + plan.logRebinds.length + plan.fishStockLogRebinds.length,
        0
      )
      const totalDeleted = legacyDuplicateMergePlans.reduce((acc, plan) => acc + plan.absorbedRows.length, 0)
      showNumericNotification(t('datatable.legacyDuplicates.toast.success', {
        kept: String(legacyDuplicateMergePlans.length),
        deleted: String(totalDeleted),
        rebinds: String(totalRebinds)
      }))
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error)
      setLegacyDuplicateMigrationError(
        t('datatable.legacyDuplicates.toast.failure', { reason })
      )
    } finally {
      setLegacyDuplicateMigrationRunning(false)
    }
  }

  const deleteRowById = (id: string, reason = '') => {
    if (!canEditProductionRecords) {
      showNumericNotification(t('datatable.readonly.cannotDeleteRecords'))
      return
    }

    // I-2 delete-parity via the shared plan+execute helper — the SAME path as
    // Biomass `deleteLogRow`, so the two delete surfaces cannot drift. This is
    // also what closes the T3-D `HarvestLog` gap: harvest was added after
    // findings #8/#67 wired the other three streams here, so a harvest-bearing
    // row deleted from Database used to orphan its `HarvestLog` events.
    // Optimistic (Database's established style): drop the linked events from
    // local state now, fire the deletes in the background.
    // See src/screens/biomass/productionRecordCascadeDelete.ts + I-2.
    const rowForLink = (data as any[]).find((r) => String(r?.id) === String(id))
    const linkedEventPlan = planProductionRecordLinkedEventDeletes({
      targetRow: rowForLink ?? { id, date: '', lot: '' },
      ...logsOfRowLot({ id })
    })
    if (linkedEventPlan.mortalityLogIds.length > 0) {
      const ids = new Set(linkedEventPlan.mortalityLogIds)
      setMortalityLogs((prev) => prev.filter((log) => !ids.has(log.id)))
    }
    if (linkedEventPlan.weightSampleLogIds.length > 0) {
      const ids = new Set(linkedEventPlan.weightSampleLogIds)
      setWeightSampleLogs((prev) => prev.filter((log) => !ids.has(log.id)))
    }
    if (linkedEventPlan.fishStockLogIds.length > 0) {
      const ids = new Set(linkedEventPlan.fishStockLogIds)
      setFishStockLogs((prev) => prev.filter((log) => !ids.has(log.id)))
    }
    if (linkedEventPlan.harvestLogIds.length > 0) {
      const ids = new Set(linkedEventPlan.harvestLogIds)
      setHarvestLogs((prev) => prev.filter((log) => !ids.has(log.id)))
    }
    void executeProductionRecordLinkedEventDeletes(linkedEventPlan, {
      reason: t('productionRow.delete.auditReason', { reason: reason || t('productionRow.delete.noLinkedReason') })
    }).catch((error) => {
      console.error('Datatable: linked Tier A event delete failed', error)
    })

    void dequeueProductionRow(id)

    setData((prev) => removeRowById(prev as any[], id))
    setInvalidDates((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })

    if (selectedFarmAndUnit) {
      const context: ProductionUnitTableContext = {
        farmId: selectedFarmAndUnit.farmId,
        productionUnitId: selectedFarmAndUnit.productionUnitId,
        farmName: selectedFarmAndUnit.farmName,
        productionUnitName: selectedFarmAndUnit.productionUnitName
      }
      const existing = loadProductionUnitTableRows(context)
      saveProductionUnitTableRows(context, removeRowById(existing as any[], id))
    } else {
      deleteProductionUnitTableRowEverywhere(id)
    }
  }

  return (
    <div className="responsive-container">
      <h2 className="h2">{t('datatable.title')}</h2>
      <p className="lead">
        {leadText}
      </p>

      <CurrentContextBar
        species={{ value: speciesPresetLabel }}
        fields={[
          { label: t('datatable.context.facility'), value: selectedFarmLabel },
          { label: t('datatable.context.productionUnit'), value: selectedProductionUnitLabel }
        ]}
      />

      <div className="responsive-toolbar">
        <ContextSelectorBar
          farm={{
            label: t('datatable.context.facility'),
            value: farmId,
            onChange: (value) => {
              setFarmId(value)
              setProductionUnitId('')
              setLotFilter('')
              setAvailableLots([])
              setLotAutoClearedIds(new Set())
              setLotImportNotice(null)
              setNumericNotification(null)
              setShowDateNotification(false)
              setIsContextSwitching(true)

              const selectedFarm = availableFarms.find((farm: any) => String(farm.id ?? farm.name ?? '') === String(value))
              const farmSpeciesKey = resolveFarmProductionSpeciesKey(selectedFarm)
              if (farmSpeciesKey) {
                setSpeciesKey(farmSpeciesKey)
              }
            },
            placeholder: t('datatable.filter.allFacilities'),
            options: availableFarms.map((farm: any) => ({
              key: String(farm.id ?? farm.name ?? ''),
              value: String(farm.id ?? farm.name ?? ''),
              label: farm.name
            }))
          }}
          productionUnit={{
            label: t('datatable.context.productionUnit'),
            value: productionUnitId,
            onChange: (value) => {
              setProductionUnitId(value)
              setLotFilter('')
              setAvailableLots([])
              setLotAutoClearedIds(new Set())
              setLotImportNotice(null)
              setNumericNotification(null)
              setShowDateNotification(false)
              setIsContextSwitching(true)
            },
            disabled: !farmId || availableProductionUnits.length === 0,
            placeholder: t('datatable.filter.allProductionUnitsShort'),
            options: buildUnitSelectOptions(availableProductionUnits, t('unit.inactive.suffix'))
          }}
          lot={{
            label: t('datatable.context.lot'),
            value: activeLotFilter,
            onChange: setLotFilter,
            disabled: lotFilterOptions.length === 0,
            placeholder: t('datatable.filter.allLots'),
            options: lotFilterOptions.map((option) => ({ key: option.value, value: option.value, label: option.label }))
          }}
          summary={null}
        />
        
        <div className="responsive-actions">
          <GradientButton onClick={exportCSV} variant="info" size="sm">{t('datatable.action.exportCsv')}</GradientButton>
          {/* v2.2 T3-C-04: the CSV import launcher moved to Biomass (the Tier A
              operational-entry surface for the biology streams the importer writes).
              Database is Tier B/E — it retains Export CSV, Fusionar duplicados,
              Refresh / Retry sync, per-row Delete, cell edit for non-biology columns,
              and read-only trust popovers. See docs/mvp-2-2-architecture-cleanup-plan.md
              card T3-C-04 + /memories/repo/biomass-csv-import-launcher.md. */}
          {legacyDuplicateMergePlans.length > 0 && (
            <GradientButton
              onClick={() => {
                setLegacyDuplicateMigrationError(null)
                setLegacyDuplicateMigrationOpen(true)
              }}
              variant="warning"
              size="sm"
              disabled={!canEditProductionRecords || !selectedProductionContext}
              title={t('datatable.action.fuseLegacyDuplicatesTitle')}
            >
              {t('datatable.action.fuseLegacyDuplicates', { count: String(legacyDuplicateMergePlans.length) })}
            </GradientButton>
          )}
        </div>
        {!selectedFarmAndUnit && (
          <div style={{ marginTop: '6px', fontSize: '12px', color: '#6c757d' }}>
            ⓘ {t('datatable.hint.selectContextForImport')}
          </div>
        )}

        {selectedFarmAndUnit ? (
          <div style={{ marginTop: '8px', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px', backgroundColor: '#eff6ff', width: '100%' }}>
            <div style={{ fontSize: '13px', color: '#1e3a8a', fontWeight: 700, marginBottom: '6px' }}>{t('datatable.sync.title')}</div>
            {productionSyncAttentionMessage ? (
              <div style={{ fontSize: '12px', color: '#1e40af', marginBottom: '6px' }}>{productionSyncAttentionMessage}</div>
            ) : null}
            {productionSyncLastResultSummary ? (
              <div style={{ fontSize: '12px', color: '#1e3a8a', marginBottom: '6px' }}>
                <div>{t('datatable.sync.lastResult', { result: productionSyncLastResultSummary.resultLabel })}</div>
                <div>{t('datatable.sync.lastAttempted', { time: productionSyncLastResultSummary.attemptedAtLabel })}</div>
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <GradientButton
                onClick={() => void handleRetryProductionSync()}
                variant="secondary"
                size="sm"
                loading={retryingProductionSync}
                disabled={retryingProductionSync || refreshingProductionSync}
              >
                {t('datatable.sync.retryAction')}
              </GradientButton>
              <GradientButton
                onClick={() => void handleRefreshProductionSync()}
                variant="info"
                size="sm"
                loading={refreshingProductionSync}
                disabled={refreshingProductionSync || retryingProductionSync}
              >
                {t('datatable.sync.refreshAction')}
              </GradientButton>
            </div>
          </div>
        ) : null}
      </div>
      {importReviewNotice ? (
        <div style={{
          marginBottom: '12px',
          padding: '10px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '16px' }}>ℹ️</span>
          <div style={{ color: '#166534', flex: 1 }}>{importReviewNotice}</div>
        </div>
      ) : null}
      {numericNotification && (
        <div style={{
          marginBottom: '12px',
          padding: '10px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c2c7',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '16px' }}>⛔</span>
          <div style={{ color: '#842029', flex: 1 }}>{numericNotification}</div>
          <button
            onClick={dismissNumericNotification}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              fontSize: '16px',
              cursor: 'pointer',
              color: '#842029'
            }}
            title={t('datatable.notification.closeTitle')}
          >
            ✕
          </button>
        </div>
      )}
      {lotImportNotice && (
        <div style={{
          marginBottom: '12px',
          padding: '10px',
          backgroundColor: '#e7f3ff',
          border: '1px solid #b3d7ff',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '16px' }}>ℹ️</span>
          <div style={{ color: '#0056b3', flex: 1 }}>
            <strong>{t('datatable.lotNotice.label')}</strong> {lotImportNotice}
          </div>
          <button
            onClick={() => setLotImportNotice(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              fontSize: '16px',
              cursor: 'pointer',
              color: '#0056b3'
            }}
            title={t('datatable.notification.closeTitle')}
          >
            ✕
          </button>
        </div>
      )}
      {showDateNotification && (
        <div style={{
          marginBottom: '12px',
          padding: '10px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '16px' }}>⚠️</span>
          <div>
            <strong style={{ color: '#856404' }}>{t('datatable.dateNotice.title')}</strong>
            <span style={{ color: '#856404', marginLeft: '6px' }}>{t('datatable.dateNotice.message')}</span>
          </div>
          <button
            onClick={() => setShowDateNotification(false)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              fontSize: '16px',
              cursor: 'pointer',
              color: '#856404'
            }}
            title={t('datatable.notification.closeTitle')}
          >
            ✕
          </button>
        </div>
      )}
      <div className={isContextSwitching ? 'table-shell table-shell-switching' : 'table-shell table-shell-ready'} style={{ maxWidth: '1200px', width: '100%', margin: '0 auto' }}>
        <div
          ref={tableScrollTopRef}
          onScroll={syncScroll('top')}
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            border: '2px solid #ddd',
            borderBottom: 'none',
            borderRadius: '8px 8px 0 0',
            marginTop: 24,
            height: 12,
            background: '#f8f9fa'
          }}
        >
          <div
            style={{
              width: Math.max(tableWidth, tableScrollRef.current?.clientWidth || 0),
              height: 1,
            }}
          />
        </div>

        <div
          ref={tableScrollRef}
          onScroll={syncScroll('main')}
          style={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            border: '2px solid #ddd',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          {hiddenMovedLotRowCount > 0 ? (
            <div data-testid="datatable-hidden-moved-lot-rows" style={{ padding: '8px 12px', fontSize: 13, color: '#475569', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              {hiddenMovedLotRowCount === 1
                ? t(showMovedLotRows ? 'datatable.movedLot.shownOne' : 'datatable.movedLot.hiddenOne')
                : t(showMovedLotRows ? 'datatable.movedLot.shown' : 'datatable.movedLot.hidden', { count: hiddenMovedLotRowCount })}{' '}
              <button
                type="button"
                onClick={() => setShowMovedLotRows((value) => !value)}
                style={{ border: 'none', background: 'none', color: '#1d4ed8', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 13 }}
                data-testid="datatable-toggle-moved-lot-rows"
              >
                {t(showMovedLotRows ? 'datatable.emptyRows.hide' : 'datatable.emptyRows.show')}
              </button>
            </div>
          ) : null}
          <DeletedLotRowsLine
            count={hiddenDeletedLotRowCount}
            shown={showDeletedLotRows}
            onToggle={() => setShowDeletedLotRows((value) => !value)}
            testId="datatable-hidden-deleted-lot-rows"
          />
          {hiddenEmptyDayRowCount > 0 ? (
            <div data-testid="datatable-hidden-empty-rows" style={{ padding: '8px 12px', fontSize: 13, color: '#475569', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              {hiddenEmptyDayRowCount === 1
                ? t(showEmptyDayRows ? 'datatable.emptyRows.shownOne' : 'datatable.emptyRows.hiddenOne')
                : t(showEmptyDayRows ? 'datatable.emptyRows.shown' : 'datatable.emptyRows.hidden', { count: hiddenEmptyDayRowCount })}{' '}
              <button
                type="button"
                onClick={() => setShowEmptyDayRows((value) => !value)}
                style={{ border: 'none', background: 'none', color: '#1d4ed8', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 13 }}
                data-testid="datatable-toggle-empty-rows"
              >
                {t(showEmptyDayRows ? 'datatable.emptyRows.hide' : 'datatable.emptyRows.show')}
              </button>
            </div>
          ) : null}
          {/* Use auto table layout to allow minWidth/maxWidth to work properly */}
          <table ref={tableRef} style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {displayColumns.map(col => {
                  const isSorted = sortConfig?.key === col
                  let dir = ''
                  if (isSorted) {
                    if (sortConfig?.direction === 'asc') dir = '▲'
                    else dir = '▼'
                  }
                  const isDateCol = col === 'date'
                  return (
                    <th
                      key={col}
                      style={{
                        ...styles.th,
                        cursor: 'pointer',
                        userSelect: 'none',
                        ...(isDateCol ? {
                          minWidth: 220,
                          maxWidth: 280,
                          whiteSpace: 'normal' as const,
                          overflow: 'visible',
                          textOverflow: 'unset',
                          wordBreak: 'break-word' as const,
                        } : {})
                      }}
                      onClick={() => {
                        setSortConfig(prev => {
                          if (prev?.key === col) {
                            return { key: col, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                          }
                          return { key: col, direction: 'asc' }
                        })
                      }}
                    >
                      {columnLabels[col] ?? (col.charAt(0).toUpperCase() + col.slice(1))} {dir}
                    </th>
                  )
                })}
                <th style={{ ...styles.th, textAlign: 'center' }}>{t('datatable.table.actions')}</th>
              </tr>
            </thead>

            <tbody>
              {visibleData.length === 0 ? (
                <tr>
                  <td colSpan={displayColumns.length + 1} style={{ textAlign: 'center', color: '#666', padding: '40px', fontStyle: 'italic' }}>
                    🐟 {t('datatable.empty.noDataTitle')}<br />{t('datatable.empty.noDataSubtitle')}
                  </td>
                </tr>
              ) : (
                visibleData.map(row0 => {
                  const row = row0 as any
                  const isDeletedLot = deletedLotRowIds.has(String(row.id))
                  const isMovedLot = movedLotDestinationByRowId.has(String(row.id))
                  return (
                    <tr
                      key={row.id}
                      style={isDeletedLot || isMovedLot ? { opacity: 0.55 } : undefined}
                      data-deleted-lot={isDeletedLot ? 'true' : undefined}
                      data-moved-lot={isMovedLot ? 'true' : undefined}
                    >
                      {displayColumns.map((col) => renderBodyCell(row, col))}

                      <td style={{ ...styles.actionCell, minWidth: 300 }}>
                        <DeleteProductionRowButton
                          t={t}
                          testId="datatable-row-delete"
                          armed={deleteRowConfirm.isPending(String(row.id))}
                          disabled={!canEditProductionRecords || isDeletedLot || isMovedLot}
                          linkedEventCount={deleteRowConfirm.isPending(String(row.id))
                            ? countPlannedLinkedEventDeletes(planProductionRecordLinkedEventDeletes({
                                targetRow: row,
                                ...logsOfRowLot(row)
                              }))
                            : 0}
                          onArm={() => deleteRowConfirm.arm(String(row.id))}
                          onCancel={() => deleteRowConfirm.reset()}
                          onConfirm={(reason) => {
                            deleteRowConfirm.reset()
                            deleteRowById(String(row.id), reason)
                          }}
                        />
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {mortalityEventsPopoverRowId ? (() => {
        const row = data.find((r: any) => String(r.id) === mortalityEventsPopoverRowId) as any
        if (!row) return null
        const events = findAllLinkedMortalityLogsForRow(row, logsOfRowLot(row).mortalityLogs)
        const rollup = mortalityRollupByRowId.get(mortalityEventsPopoverRowId)
        return (
          <MortalityEventsPopover
            events={events}
            lotLabel={String(row.lot ?? '')}
            dateLabel={String(row.date ?? '')}
            totalCount={rollup ? rollup.totalDailyMortality : String(events.reduce((sum, e) => sum + (Number.parseInt(e.mortalityCount, 10) || 0), 0))}
            farmId={farmId || undefined}
            onCorrectedEvent={async () => { await refreshCurrentMortalityLogs({ forceRemote: true }) }}
            onClose={() => setMortalityEventsPopoverRowId(null)}
          />
        )
      })() : null}
      {sameDayRowsPopoverRowId ? (() => {
        const row = data.find((r: any) => String(r.id) === sameDayRowsPopoverRowId) as any
        if (!row) return null
        // v2.1 #47 follow-up: bucket by farm-local calendar day so the popover
        // lists the operator's same-day siblings, not the rows that happen to
        // share a UTC-prefix day key after cross-UTC ISO-Z storage.
        const selectedFarm = availableFarms.find((farm: any) => String(farm?.id ?? '') === farmId)
        const timeZone = resolveTimeZone(getTimeZoneFromFarm(selectedFarm))
        const siblings = findSameDayRowsForRow(row, sameDayDedupedData as any[], { timeZone })
        return (
          <SameDayRowsPopover
            rows={siblings}
            lotLabel={String(row.lot ?? '')}
            dateLabel={String(row.date ?? '')}
            onClose={() => setSameDayRowsPopoverRowId(null)}
          />
        )
      })() : null}
      {samplingEventsPopoverRowId ? (() => {
        const row = data.find((r: any) => String(r.id) === samplingEventsPopoverRowId) as any
        if (!row) return null
        return (
          <SamplingEventsPopover
            productionRowId={samplingEventsPopoverRowId}
            farmId={farmId || undefined}
            productionUnitId={productionUnitId || undefined}
            lotLabel={String(row.lot ?? '')}
            dateLabel={String(row.date ?? '')}
            onCorrectedEvent={async () => { await refreshCurrentWeightSampleLogs({ forceRemote: true }) }}
            onClose={() => setSamplingEventsPopoverRowId(null)}
          />
        )
      })() : null}
      {legacyDuplicateMigrationOpen && (
        <LegacyDuplicateMigrationModal
          plans={legacyDuplicateMergePlans}
          running={legacyDuplicateMigrationRunning}
          errorMessage={legacyDuplicateMigrationError}
          onConfirm={runLegacyDuplicateMigration}
          onCancel={() => {
            if (legacyDuplicateMigrationRunning) return
            setLegacyDuplicateMigrationOpen(false)
            setLegacyDuplicateMigrationError(null)
          }}
        />
      )}
    </div>
  )
}

const removeRowById = (rows: any[], id: string) => {
  const out: any[] = []
  for (const r of rows) {
    if (String(r?.id) !== String(id)) out.push(r)
  }
  return out
}